import {
  imeiOfDeviceId,
  isValidImei,
  SAMPLE_CALIBRATION,
  TELTONIKA_HW,
  calibrationIssues,
  defaultAnalogCalibration,
  deviceIsTeltonika,
  type BatteryResult,
  type EcoResult,
  type LiftResult,
  type VehicleAnalogCalibration,
  DVR_MEDIA,
  effectiveDvrSettings,
  type DeviceHealth,
  type DvrSettings,
  PUSH_MAX_DEVICES,
  validPushSubscription,
  type PushSubscriptionInput,
  ACK_NOTE_MAX,
  LIVE_WATCH,
  ALERT_TIME_ZONE,
  dayRecipients,
  effectiveAlertHours,
  effectiveNightContacts,
  isWorkTime,
  localParts,
  memberSeesSite,
  nextDigestAt,
  parseHm,
  routeAlarm,
  zonedTimeToUtc,
  type Alarm,
  type AlarmNotification,
  type AlarmType,
  type Geofence,
  type LonLat,
  GEOFENCE_LIMITS,
  validateGeofence,
  normalizeGeofenceName,

  type SiteAlertSettings,
  type SiteNightContact,
  authorizedEntries,
  businessDate,
  CARD_TECHS,
  DEVICE_ID_PATTERN,
  DOCUMENT_KINDS,
  normalizeCardUid,
  roleAtLeast,
  VEHICLE_TYPES,
  type CardStatus,
  type Operator,
  type OperatorCard,
  type OperatorDocument,
  type OperatorPermission,
  type CurrentUser,
  type Device,
  type Invitation,
  type InvitationPreview,
  type InvitationStatus,
  type LiveSnapshot,
  type Membership,
  type Site,
  type Tenant,
  type TenantRole,
  type Vehicle,
  type ChecklistAssignment,
  type ChecklistCategory,
  type ChecklistRecipient,
  type ChecklistTemplate,
  type SiteShift,
  deviceHasCardReader,
} from "@fts/core";
import { demoModuleData, withDemoBattery } from "./demo-modules";
import type { ModuleSettingsInput, VehicleModuleData, VehicleModuleSettings } from "./modules";
import {
  DataError,
  type SignInOptions,
  type AlarmQuery,
  type AlertPerson,
  type AlertSettingsInput,
  type ContactCandidate,
  type EffectiveAlertSettings,
  type EffectiveNightContacts,
  type AlertRecipientsStatus,
  type PersonName,
  type UiLocale,
  type PushConfig,
  type PushDevice,
  type PushTestResult,
  type GeofenceInput,
  type AuthlistStatus,
  type CardInput,
  type DocumentInput,
  type LiveOperator,
  type LiveWatch,
  type OperatorData,
  type OperatorInput,
  type PermissionInput,
  type DataProvider,
  type DeviceRegistration,
  type InvitationInput,
  type LiveFeedStatus,
  type Member,
  type RegistrationResult,
  type RevocationResult,
  type RotationResult,
  type SiteInput,
  type Unsubscribe,
  type VehicleInput,
  type ChecklistFault,
  type ChecklistForm,
  type ChecklistRecipientInput,
  type ChecklistSubmission,
  type ChecklistSubmissionDetail,
  type ChecklistSubmitInput,
  type ChecklistSubmitResult,
  type ChecklistTemplateInput,
  type ChecklistToken,
  type EffectiveChecklistRecipients,
  type FaultQuery,
  type ShiftInput,
  type SubmissionQuery,
  type AnalogCalibrationRecord,
  type DeviceImmobilizer,
  type TeltonikaSyncStatus,
} from "./provider";
import { DemoChecklist } from "./demo-checklist";
import { DemoReports } from "./demo-reports";
import type { BatteryQuery, DrivingQuery, EventReportQuery, HeatmapQuery, KysFr11Query, KysFr12Query, ReportExportEntry, ReportQuery, RouteQuery, SpaghettiQuery, TelematicsQuery } from "./reports";
import { DemoKys } from "./demo-kys";
import type { KysApproverRole, KysFormRef, KysFr11Row, KysFr12Form, MaintenanceRecord } from "@fts/core";
import type { MaintenancePlanInput, MaintenanceRecordInput } from "./provider";
import type { ChecklistComplianceRow, DrivingResult, EventRow, ExportFormat, HeatmapResult, KpiPanel, KpiPeriod, ReportResult, RoutePoint, SpaghettiResult, UsageRow } from "@fts/core";

/*
 * SAMPLE DATA ONLY. Every tenant, site, person, serial and device id below is made up
 * for the demo mode. Names carry "Örnek"/"(örnek)" and e-mails use the reserved
 * ".example" TLD so nothing can be mistaken for real customer or personal data.
 *
 * The write methods mirror the rules of the SQL functions and RLS policies in
 * supabase/migrations (tenancy + devices/invitations) and throw the same DbError tokens.
 */

export const DEMO_PASSWORD = "demo";

const T1 = "t-ornek-lojistik";
const T2 = "t-expartise-kartepe";

const TENANTS: Tenant[] = [
  { id: T1, name: "Örnek Lojistik A.Ş.", slug: "ornek-lojistik", createdAt: "2026-10-01T09:00:00Z" },
  { id: T2, name: "Kartepe Atölyesi (örnek pilot)", slug: "kartepe-ornek", createdAt: "2026-10-01T09:00:00Z" },
];

const site = (
  id: string,
  tenantId: string,
  parentId: string | null,
  name: string,
  kind: Site["kind"],
  address: string | null = null,
  pos: [number, number] | null = null,
): Site => ({ id, tenantId, parentId, name, kind, address, lat: pos?.[0] ?? null, lon: pos?.[1] ?? null });

const SITES: Site[] = [
  // Coordinates are approximate town-level sample points, not real facilities.
  site("s-gebze", T1, null, "Gebze Dağıtım Merkezi (örnek)", "site", "Örnek Sanayi Bölgesi, Gebze / Kocaeli", [40.8006, 29.4402]),
  site("s-gebze-a", T1, "s-gebze", "Depo A", "warehouse", null, [40.8012, 29.4389]),
  site("s-gebze-b", T1, "s-gebze", "Depo B", "warehouse", null, [40.7999, 29.4418]),
  site("s-gebze-b-ship", T1, "s-gebze-b", "Sevkiyat alanı", "department"),
  site("s-izmir", T1, null, "İzmir Aktarma Deposu (örnek)", "site", "Örnek Lojistik Köyü, Kemalpaşa / İzmir", [38.4262, 27.4185]),
  site("s-kartepe", T2, null, "Kartepe Atölyesi (örnek)", "site", "Kartepe / Kocaeli", [40.7530, 30.0230]),
];

let vehicleSeq = 0;
const vehicle = (
  tenantId: string,
  siteId: string,
  fleetNo: string,
  type: Vehicle["type"],
  power: Vehicle["power"],
  model: string,
  batteryVoltage: number | null,
  deviceId: string | null,
  active = true,
): Vehicle => {
  vehicleSeq += 1;
  const n = String(vehicleSeq).padStart(4, "0");
  return {
    id: `v-${tenantId === T1 ? "ol" : "kt"}-${fleetNo.toLowerCase()}`,
    tenantId,
    siteId,
    fleetNo,
    type,
    power,
    make: "Örnek Üretici",
    model,
    serialNo: `SMP-${n}`,
    batteryVoltage,
    active,
    unauthorizedStartAlarm: true,
    deviceId,
  };
};

const VEHICLES: Vehicle[] = [
  // Gebze — Depo A
  vehicle(T1, "s-gebze-a", "FL-01", "forklift", "electric", "EFX 25", 48, "DEV-SMP-1001"),
  vehicle(T1, "s-gebze-a", "FL-02", "forklift", "electric", "EFX 25", 48, "DEV-SMP-1002"),
  vehicle(T1, "s-gebze-a", "RT-01", "reach_truck", "electric", "RX 16", 48, "DEV-SMP-1003"),
  vehicle(T1, "s-gebze-a", "RT-02", "reach_truck", "electric", "RX 16", 48, null),
  vehicle(T1, "s-gebze-a", "OP-01", "order_picker", "electric", "OPX 10", 24, "DEV-SMP-1004"),
  // Gebze — Depo B
  vehicle(T1, "s-gebze-b", "FL-03", "forklift", "electric", "EFX 30", 80, "DEV-SMP-1005"),
  vehicle(T1, "s-gebze-b", "FL-04", "forklift", "lpg", "GFX 30", null, "DEV-SMP-1006"),
  vehicle(T1, "s-gebze-b", "RT-03", "reach_truck", "electric", "RX 20", 48, "DEV-SMP-1007"),
  vehicle(T1, "s-gebze-b", "OP-02", "order_picker", "electric", "OPX 10", 24, null),
  // Gebze — Depo B / Sevkiyat alanı
  vehicle(T1, "s-gebze-b-ship", "FL-05", "forklift", "diesel", "DFX 35", null, "DEV-SMP-1008"),
  vehicle(T1, "s-gebze-b-ship", "TP-01", "pallet_truck", "electric", "PTX 20", 24, null),
  // Gebze — site level (spare, out of service)
  vehicle(T1, "s-gebze", "FL-06", "forklift", "electric", "EFX 25", 48, null, false),
  // İzmir
  vehicle(T1, "s-izmir", "FL-21", "forklift", "electric", "EFX 25", 48, "DEV-SMP-1101"),
  vehicle(T1, "s-izmir", "RT-21", "reach_truck", "electric", "RX 16", 48, null),
  // Kartepe pilot — two sample Teltonika (FMC130) units (ADR 0034; fictional IMEIs with valid check digits),
  // the other devices not installed yet.
  vehicle(T2, "s-kartepe", "FL-01", "forklift", "electric", "EFX 25", 48, "TLT-353201350896384"),
  vehicle(T2, "s-kartepe", "FL-02", "forklift", "electric", "EFX 30", 80, "TLT-490154203237526"),
  vehicle(T2, "s-kartepe", "FL-03", "forklift", "diesel", "DFX 35", null, null),
  vehicle(T2, "s-kartepe", "FL-04", "forklift", "lpg", "GFX 25", null, null),
  vehicle(T2, "s-kartepe", "FL-05", "forklift", "electric", "EFX 16", 48, null),
];

/** Claim codes printed on the labels of the sample inventory devices (shown as a hint in demo mode). */
export const DEMO_CLAIM_CODES: Record<string, string> = {
  "FTS-900001": "0A1B-2C3D-4E5F",
  "FTS-900002": "9F8E-7D6C-5B4A",
};

/** Sample devices. Times are relative to the moment the provider is created. */
function seedDevices(now: number): Device[] {
  const ago = (min: number) => new Date(now - min * 60_000).toISOString();
  const base = {
    hw: "FTS-TU1 (örnek)",
    imei: null,
    iccid: null,
    revoked: false,
    hasCardReader: true,
  };
  const paired = VEHICLES.filter((v) => v.deviceId).map<Device>((v, i) => ({
    ...base,
    deviceId: v.deviceId!,
    tenantId: v.tenantId,
    vehicleId: v.id,
    fw: v.deviceId!.startsWith("TLT-") ? "codec8e" : i % 3 === 2 ? "1.3.0" : "1.4.2",
    ...(v.deviceId!.startsWith("TLT-") ? { hw: TELTONIKA_HW } : {}),
    imei: v.deviceId!.startsWith("TLT-") ? v.deviceId!.slice(4) : `35000000000${String(1000 + i).padStart(4, "0")}`,
    // Most sample devices report regularly; two have gone quiet.
    online: i !== 3 && i !== 6,
    lastSeenAt: i === 3 ? ago(60 * 26) : i === 6 ? ago(95) : ago(1 + i),
    claimedAt: "2026-10-05T08:30:00Z",
  }));
  return [
    ...paired,
    // Claimed by Örnek Lojistik, waiting to be installed on a vehicle.
    { ...base, deviceId: "FTS-900010", tenantId: T1, vehicleId: null, fw: "1.4.2", online: false, lastSeenAt: null, claimedAt: "2026-10-14T10:00:00Z" },
    // Expartise inventory, not claimed by anyone yet.
    { ...base, deviceId: "FTS-900001", tenantId: null, vehicleId: null, fw: null, online: false, lastSeenAt: null, claimedAt: null },
    { ...base, deviceId: "FTS-900002", tenantId: null, vehicleId: null, fw: null, online: false, lastSeenAt: null, claimedAt: null },
  ];
}

/**
 * Sample live states for the paired demo devices, around their site. One of each map state:
 * working, idle, off, offline, an impact alarm and a vehicle indoors (last known position).
 */
function seedLive(now: number, devices: Device[], sites: Site[], vehicles: Vehicle[]): LiveSnapshot[] {
  const iso = (secAgo: number) => new Date(now - secAgo * 1000).toISOString();
  const anchor = (siteId: string): [number, number] | null => {
    let cur = sites.find((x) => x.id === siteId);
    while (cur) {
      if (cur.lat !== null && cur.lon !== null) return [cur.lat, cur.lon];
      const parentId: string | null = cur.parentId;
      cur = parentId ? sites.find((x) => x.id === parentId) : undefined;
    }
    return null;
  };
  type Pattern = { ign: boolean; spd: number; heardSec: number; posSec: number | null; event?: [string, number] };
  const patterns: Pattern[] = [
    { ign: true, spd: 7, heardSec: 4, posSec: 4 },
    { ign: true, spd: 0, heardSec: 9, posSec: 9 },
    { ign: true, spd: 5, heardSec: 3, posSec: 3 },
    { ign: false, spd: 0, heardSec: 60 * 26 * 60, posSec: 60 * 26 * 60 }, // device offline for a day
    { ign: false, spd: 0, heardSec: 40, posSec: 40 },
    { ign: true, spd: 0, heardSec: 6, posSec: 6, event: ["impact", 180] },
    { ign: false, spd: 0, heardSec: 95 * 60, posSec: 95 * 60 }, // device offline
    { ign: true, spd: 0, heardSec: 12, posSec: 7 * 60 }, // indoors: GNSS lost 7 minutes ago
    { ign: true, spd: 9, heardSec: 2, posSec: 2 },
  ];
  const out: LiveSnapshot[] = [];
  devices
    .filter((d) => d.vehicleId)
    .forEach((d, i) => {
      const v = vehicles.find((x) => x.id === d.vehicleId);
      const a = v ? anchor(v.siteId) : null;
      if (!v || !a) return;
      const p = patterns[i % patterns.length]!;
      // Spread the vehicles over the yard (~±120 m) deterministically.
      const dLat = (((i * 37) % 11) - 5) * 0.0002;
      const dLon = (((i * 53) % 13) - 6) * 0.00025;
      out.push({
        deviceId: d.deviceId,
        vehicleId: v.id,
        ts: iso(p.heardSec),
        receivedAt: iso(p.heardSec),
        lat: a[0] + dLat,
        lon: a[1] + dLon,
        posAt: p.posSec === null ? null : iso(p.posSec),
        posAcc: 4,
        posSrc: "gnss",
        speedKmh: p.spd,
        ignition: p.ign,
        seat: p.ign,
        belt: p.ign,
        operatorUid: p.ign ? `04A1B2${String(10 + i)}` : null,
        // Sample mix so every connection label shows up in the demo.
        link: i % 3 === 0 ? "wifi" : i % 3 === 1 ? "lte" : "gsm",
        rssi: -70 - i,
        supplyV: v.batteryVoltage,
        lastEventType: p.event?.[0] ?? null,
        lastEventAt: p.event ? iso(p.event[1]) : null,
        lastAlarmType: p.event?.[0] ?? null,
        lastAlarmAt: p.event ? iso(p.event[1]) : null,
      });
    });
  return out;
}


/**
 * Sample operators (fictional people, "Örnek" surnames). Dates are relative to the provider's start so the
 * demo always shows one of each state: valid, expiring soon, suspended (expired certificate), left.
 * Card UIDs match the sample live states so the live map shows names.
 */
function seedOperators(now: number): OperatorData {
  const today = businessDate(new Date(now));
  const day = (n: number) => new Date(Date.parse(`${today}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
  const op = (id: string, fullName: string, employeeNo: string, siteId: string | null, extra: Partial<Operator> = {}): Operator => ({
    id, tenantId: T1, siteId, fullName, employeeNo, active: true, leftOn: null, anonymized: false, ...extra,
  });
  const operators: Operator[] = [
    op("op-ali", "Ali Örnek", "P-1001", "s-gebze"),
    op("op-elif", "Elif Örnek", "P-1002", "s-gebze"),
    op("op-burak", "Burak Örnek", "P-1003", "s-gebze"),
    op("op-selin", "Selin Örnek", "P-1004", "s-gebze"),
    op("op-murat", "Murat Örnek", "P-1005", "s-gebze"),
    op("op-deniz", "Deniz Örnek", "P-1006", "s-izmir"),
    op("op-hasan", "Hasan Örnek", "P-1007", "s-gebze", { leftOn: day(-20) }),
  ];
  const card = (id: string, operatorId: string, uid: string, status: CardStatus = "active", tech: OperatorCard["tech"] = "mifare"): OperatorCard => ({
    id, tenantId: T1, operatorId, uid, tech, status, createdAt: "2026-10-10T08:00:00Z",
  });
  const cards: OperatorCard[] = [
    card("c-1", "op-ali", "04A1B210"),
    card("c-2", "op-elif", "04A1B211"),
    card("c-3", "op-burak", "04A1B212"),
    card("c-4", "op-selin", "04A1B215"),
    card("c-5", "op-murat", "04A1B217", "active", "hid"),
    card("c-6", "op-deniz", "04A1B218"),
    card("c-7", "op-ali", "04A1B2FF", "lost"),
    card("c-8", "op-hasan", "04A1B220", "revoked"),
  ];
  const doc = (id: string, operatorId: string, validUntil: string, kind: OperatorDocument["kind"] = "operator_certificate", required = true): OperatorDocument => ({
    id, tenantId: T1, operatorId, kind, label: null, validUntil, required,
  });
  const documents: OperatorDocument[] = [
    doc("d-1", "op-ali", day(400)),
    doc("d-2", "op-elif", day(12)),
    doc("d-3", "op-burak", day(250)),
    doc("d-4", "op-selin", day(-3)),
    doc("d-5", "op-murat", day(180)),
    doc("d-6", "op-murat", day(25), "training", false),
    doc("d-7", "op-deniz", day(300)),
  ];
  const perm = (id: string, operatorId: string, p: Partial<OperatorPermission>): OperatorPermission => ({
    id, tenantId: T1, operatorId, siteId: null, vehicleType: null, vehicleId: null, validUntil: null, ...p,
  });
  const permissions: OperatorPermission[] = [
    perm("p-1", "op-ali", { siteId: "s-gebze" }),
    perm("p-2", "op-elif", { siteId: "s-gebze-a", vehicleType: "forklift" }),
    perm("p-3", "op-burak", { siteId: "s-gebze-b" }),
    perm("p-4", "op-selin", { siteId: "s-gebze" }),
    perm("p-5", "op-murat", { vehicleId: "v-ol-fl-05", validUntil: day(60) }),
    perm("p-6", "op-deniz", { siteId: "s-izmir" }),
    perm("p-7", "op-hasan", { siteId: "s-gebze" }),
  ];
  return { operators, cards, documents, permissions };
}

/* ---------------- Sprint 6a: sample alarms and notification settings ---------------- */

/** Gebze has its own (sample) hours, Mon–Sat 07:00–19:00; its warehouses inherit them. İzmir uses the defaults. */
function seedAlertSettings(): SiteAlertSettings[] {
  return [{ tenantId: T1, siteId: "s-gebze", workDays: [1, 2, 3, 4, 5, 6], workStart: "07:00", workEnd: "19:00", tz: ALERT_TIME_ZONE }];
}

/** Gebze's night contact is the sample fleet manager; İzmir has none (off-hours impacts go to day recipients). */
function seedNightContacts(): SiteNightContact[] {
  return [{ tenantId: T1, siteId: "s-gebze", userId: "u-mehmet" }];
}

type AlarmSeed = {
  id: string;
  vehicleId: string;
  type: AlarmType;
  severity?: number;
  /** [days ago, local time] in Istanbul, or seconds ago. */
  at: [number, string] | number;
  operatorId?: string | null;
  /** Minutes between the event and its arrival (buffered data). */
  lateMin?: number;
  /** Buffered with an unverified device clock: occurred = received. */
  timeUnknown?: boolean;
  /** Same-type repeats folded into the alarm, the last one this many minutes after the first. */
  repeats?: { count: number; lastAfterMin: number };
  ack?: { by: string; afterMin: number; note?: string };
  /** Geofence alarms: the zone and the violation point (metres east/north of the zone's site). */
  geofence?: { id: string; east: number; north: number };
};

/** Fictional sample alarms on the sample vehicles; times are relative to the provider's start. */
const ALARM_SEEDS: AlarmSeed[] = [
  // Matches the impact on the live map (FL-04, three minutes ago, sample operator Selin Örnek).
  { id: "101", vehicleId: "v-ol-fl-04", type: "impact", severity: 3, at: 180, operatorId: "op-selin" },
  { id: "102", vehicleId: "v-ol-fl-01", type: "impact", severity: 2, at: [1, "02:40"], operatorId: "op-ali" },
  {
    id: "103",
    vehicleId: "v-ol-rt-01",
    type: "seat_violation",
    at: [2, "10:15"],
    operatorId: "op-burak",
    ack: { by: "u-zeynep", afterMin: 25, note: "Örnek not: operatörle konuşuldu, koltuk sensörü kontrol edildi." },
  },
  { id: "104", vehicleId: "v-ol-fl-05", type: "unauthorized_start", at: [3, "23:05"], operatorId: null },
  { id: "105", vehicleId: "v-ol-op-01", type: "card_denied", at: [1, "14:20"], operatorId: null, lateMin: 5 * 60 },
  { id: "106", vehicleId: "v-ol-fl-21", type: "belt_violation", at: [2, "09:30"], operatorId: "op-deniz", repeats: { count: 3, lastAfterMin: 52 } },
  { id: "107", vehicleId: "v-ol-fl-03", type: "power_loss", at: [4, "21:50"], operatorId: null, ack: { by: "u-mehmet", afterMin: 11 * 60 } },
  {
    id: "108",
    vehicleId: "v-ol-rt-03",
    type: "tamper",
    at: [6, "11:00"],
    operatorId: "op-murat",
    ack: { by: "u-zeynep", afterMin: 40, note: "Örnek not: kablo bağlantısı servis tarafından yenilendi." },
  },
  // Sprint 6b: a sample forklift drove into the sample pedestrian walkway (forbidden zone).
  { id: "109", vehicleId: "v-ol-fl-03", type: "geofence_enter", at: [1, "10:40"], operatorId: "op-elif", geofence: { id: "gf-walkway", east: 6, north: 1 } },
];

/** Same checks as fts_night_level(): the contact is a member who sees the site and has an e-mail address. */
function nightChecks(sites: Site[], members: Membership[], users: DemoUser[], siteId: string) {
  return {
    sees: (u: string) => members.some((m) => m.userId === u && memberSeesSite(sites, m, siteId)),
    reachable: (u: string) => !!users.find((x) => x.id === u)?.email.trim(),
  };
}

/**
 * Same as site_alert_recipients_status(): reachable (with an e-mail) day recipients — safety and fleet
 * managers who see the site, plus the admins who see it when the tenant asked for a copy of every alarm
 * (alarm_copy_admins) or when none of the others is reachable — and night recipients (the night contacts
 * that apply, plus those admins with the copy setting; else the day recipients) and the admins who get a
 * copy because of the setting. Counts only.
 */
function recipientsStatus(
  sites: Site[],
  members: Membership[],
  users: DemoUser[],
  contacts: SiteNightContact[],
  siteId: string,
  copyAdmins = false,
): AlertRecipientsStatus {
  const reachable = (u: string) => !!users.find((x) => x.id === u)?.email.trim();
  const seeing = members.filter((m) => memberSeesSite(sites, m, siteId));
  const staff = seeing.filter((m) => (m.role === "safety" || m.role === "fleet_manager") && reachable(m.userId)).map((m) => m.userId);
  const admins = seeing.filter((m) => m.role === "admin" && reachable(m.userId)).map((m) => m.userId);
  const adminsFallback = staff.length === 0 && !copyAdmins;
  const day = new Set(copyAdmins || staff.length === 0 ? [...staff, ...admins] : staff);
  const night = effectiveNightContacts(sites, contacts, siteId, nightChecks(sites, members, users, siteId)).userIds;
  const nightCount = night.length > 0 ? new Set(copyAdmins ? [...night, ...admins] : night).size : day.size;
  return { dayRecipients: day.size, nightRecipients: nightCount, adminsFallback, adminCopies: copyAdmins ? admins.length : 0 };
}

function seedAlarms(
  now: number,
  sites: Site[],
  vehicles: Vehicle[],
  memberships: Membership[],
  settings: SiteAlertSettings[],
  contacts: SiteNightContact[],
): { alarms: Alarm[]; notifications: AlarmNotification[] } {
  const today = localParts(new Date(now), ALERT_TIME_ZONE).date;
  const dayAgo = (n: number) => new Date(Date.parse(`${today}T12:00:00Z`) - n * 86_400_000).toISOString().slice(0, 10);
  const alarms: Alarm[] = [];
  const notifications: AlarmNotification[] = [];
  let nseq = 0;
  for (const a of ALARM_SEEDS) {
    const v = vehicles.find((x) => x.id === a.vehicleId);
    if (!v) continue;
    const occurred = typeof a.at === "number" ? new Date(now - a.at * 1000) : zonedTimeToUtc(dayAgo(a.at[0]), a.at[1], ALERT_TIME_ZONE);
    const received = new Date(occurred.getTime() + (a.lateMin ?? 0) * 60_000 + 2000);
    const hours = effectiveAlertHours(sites, settings, v.siteId).hours;
    const offHours = !isWorkTime(hours, occurred);
    const alarm: Alarm = {
      id: a.id,
      tenantId: v.tenantId,
      siteId: v.siteId,
      vehicleId: v.id,
      deviceId: v.deviceId,
      eventId: a.type === "unauthorized_start" ? null : `9${a.id}`,
      type: a.type,
      severity: a.severity ?? 2,
      occurredAt: occurred.toISOString(),
      receivedAt: received.toISOString(),
      operatorId: a.operatorId ?? null,
      offHours,
      delayed: (a.lateMin ?? 0) > 0 || a.timeUnknown === true,
      timeUnknown: a.timeUnknown === true,
      repeatCount: a.repeats?.count ?? 0,
      lastOccurredAt: new Date(occurred.getTime() + (a.repeats?.lastAfterMin ?? 0) * 60_000).toISOString(),
      notifyError: null,
      status: a.ack ? "acknowledged" : "open",
      ackedBy: a.ack?.by ?? null,
      ackedAt: a.ack ? new Date(received.getTime() + a.ack.afterMin * 60_000).toISOString() : null,
      ackNote: a.ack?.note ?? null,
      createdAt: received.toISOString(),
      geofenceId: a.geofence?.id ?? null,
      lat: null,
      lon: null,
    };
    if (a.geofence) {
      const zone = GEOFENCE_SEEDS.find((g) => g.id === a.geofence!.id);
      const anchor = zone ? sites.find((x) => x.id === zone.siteId) : undefined;
      if (anchor && anchor.lat !== null && anchor.lon !== null) {
        const [lon, lat] = offsetM(anchor.lat, anchor.lon, a.geofence.east, a.geofence.north);
        alarm.lat = lat;
        alarm.lon = lon;
      }
    }
    alarms.push(alarm);
    const members = memberships.filter((m) => m.tenantId === v.tenantId);
    const day = dayRecipients(sites, members, v.siteId);
    const night = effectiveNightContacts(sites, contacts, v.siteId, nightChecks(sites, members, USERS, v.siteId)).userIds;
    const route = routeAlarm(a.type, offHours, day, night);
    const add = (userId: string, kind: AlarmNotification["kind"], due: Date) => {
      nseq += 1;
      const sent = due.getTime() <= now;
      notifications.push({
        id: `n-${nseq}`,
        tenantId: v.tenantId,
        alarmId: alarm.id,
        userId,
        channel: "email",
        kind,
        status: sent ? "sent" : "pending",
        dueAt: due.toISOString(),
        sentAt: sent ? new Date(due.getTime() + 20_000).toISOString() : null,
      });
    };
    for (const u of route.immediate) add(u, "immediate", received);
    const digestAt = nextDigestAt(hours, occurred);
    if (digestAt) for (const u of route.digest) add(u, "digest_item", digestAt);
    // Like fts_alarm_notify(): nobody with an e-mail address was queued.
    const reachable = (u: string) => !!USERS.find((x) => x.id === u)?.email.trim();
    if (![...route.immediate, ...(digestAt ? route.digest : [])].some(reachable)) alarm.notifyError = "no_recipients";
  }
  return { alarms, notifications };
}

/* ---------------- Sprint 6b: sample geofences (ADR 0015) ---------------- */

/** [lon, lat] at (east, north) metres from a point; 1e-7° (≈ 1 cm) precision. */
function offsetM(lat: number, lon: number, east: number, north: number): LonLat {
  const m = 111_194.93; // metres per degree of latitude (R = 6 371 000 m)
  const r7 = (v: number) => Math.round(v * 1e7) / 1e7;
  return [r7(lon + east / (m * Math.cos((lat * Math.PI) / 180))), r7(lat + north / m)];
}

type GeofenceSeed = Omit<Geofence, "coords" | "centerLat" | "centerLon" | "createdAt" | "updatedAt" | "tenantId"> & {
  /** Polygon vertices or circle centre as metres east/north of the site's sample point. */
  vertices?: Array<[number, number]>;
  center?: [number, number];
};

/** Fictional zones on the fictional demo yards ("örnek" in every name). */
const GEOFENCE_SEEDS: GeofenceSeed[] = [
  {
    id: "gf-walkway",
    siteId: "s-gebze",
    name: "Yaya yolu – ana giriş (örnek)",
    kind: "forbidden",
    shape: "polygon",
    // 12 m wide: zones must be at least 10 m wide (ADR 0015, GNSS accuracy).
    vertices: [
      [-30, -6],
      [30, -6],
      [30, 6],
      [-30, 6],
    ],
    radiusM: null,
    vehicleTypes: [],
    dwellLimitS: null,
    active: true,
    createdBy: "u-zeynep",
  },
  {
    id: "gf-loading-b",
    siteId: "s-gebze-b",
    name: "Depo B yükleme alanı (örnek)",
    kind: "work_area",
    shape: "polygon",
    vertices: [
      [-45, -40],
      [40, -40],
      [45, 0],
      [40, 35],
      [-45, 35],
    ],
    radiusM: null,
    vehicleTypes: ["forklift", "reach_truck"],
    dwellLimitS: null,
    active: true,
    createdBy: "u-mehmet",
  },
  {
    id: "gf-charging-a",
    siteId: "s-gebze-a",
    name: "Şarj istasyonu önü (örnek)",
    kind: "dwell",
    shape: "circle",
    center: [0, 0],
    radiusM: 15,
    vehicleTypes: [],
    dwellLimitS: 600,
    active: true,
    createdBy: "u-mehmet",
  },
  {
    id: "gf-izmir-ramp",
    siteId: "s-izmir",
    name: "Rampa önü (örnek)",
    kind: "forbidden",
    shape: "circle",
    center: [20, -10],
    radiusM: 10,
    vehicleTypes: [],
    dwellLimitS: null,
    active: false,
    createdBy: "u-ayse",
  },
];

function seedGeofences(sites: Site[]): Geofence[] {
  const out: Geofence[] = [];
  for (const g of GEOFENCE_SEEDS) {
    const site = sites.find((x) => x.id === g.siteId);
    if (!site || site.lat === null || site.lon === null) continue;
    const { lat, lon } = site;
    const { vertices, center, ...rest } = g;
    const c = center ? offsetM(lat, lon, center[0], center[1]) : null;
    out.push({
      ...rest,
      tenantId: site.tenantId,
      coords: vertices ? vertices.map(([e, n]) => offsetM(lat, lon, e, n)) : null,
      centerLat: c ? c[1] : null,
      centerLon: c ? c[0] : null,
      createdAt: "2026-10-18T09:00:00Z",
      updatedAt: "2026-10-18T09:00:00Z",
    });
  }
  return out;
}

type DemoAuthlist = AuthlistStatus & { hash: string };

type DemoUser = Omit<CurrentUser, "memberships">;

const USERS: DemoUser[] = [
  { id: "u-demo", email: "demo@fts.example", displayName: "Demo Kullanıcı", platformAdmin: true },
  { id: "u-ayse", email: "ayse.ornek@ornek-lojistik.example", displayName: "Ayşe Örnek", platformAdmin: false },
  { id: "u-mehmet", email: "mehmet.ornek@ornek-lojistik.example", displayName: "Mehmet Örnek", platformAdmin: false },
  { id: "u-zeynep", email: "zeynep.ornek@ornek-lojistik.example", displayName: "Zeynep Örnek", platformAdmin: false },
  { id: "u-can", email: "can.ornek@ornek-lojistik.example", displayName: "Can Örnek", platformAdmin: false },
  { id: "u-atolye", email: "atolye.ornek@kartepe.example", displayName: "Atölye Sorumlusu (örnek)", platformAdmin: false },
];

const MEMBERSHIPS: Membership[] = [
  // The demo user is a platform admin AND a tenant admin, so every screen is reachable.
  { tenantId: T2, userId: "u-demo", role: "admin", siteIds: [] },
  { tenantId: T1, userId: "u-ayse", role: "admin", siteIds: [] },
  { tenantId: T1, userId: "u-mehmet", role: "fleet_manager", siteIds: [] },
  { tenantId: T1, userId: "u-zeynep", role: "safety", siteIds: ["s-gebze"] },
  { tenantId: T1, userId: "u-can", role: "viewer", siteIds: ["s-izmir"] },
  { tenantId: T2, userId: "u-atolye", role: "fleet_manager", siteIds: [] },
];

/** Accounts listed on the login screen so reviewers can try each role. */
export const DEMO_ACCOUNTS: Array<{ email: string; role: Membership["role"] }> = [
  { email: "ayse.ornek@ornek-lojistik.example", role: "admin" },
  { email: "mehmet.ornek@ornek-lojistik.example", role: "fleet_manager" },
  { email: "zeynep.ornek@ornek-lojistik.example", role: "safety" },
  { email: "can.ornek@ornek-lojistik.example", role: "viewer" },
];

const SESSION_KEY = "fts.demo.userId";
const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,40}$/;
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
/** Demo only: no e-mail is sent; this code signs any sample account in (shown on the login screen). */
export const DEMO_SIGNIN_CODE = "123456";
const BATTERY_VOLTAGES = [12, 24, 36, 48, 72, 80, 96];
const INVITE_TTL_MS = 7 * 24 * 3600 * 1000;

type DemoInvitation = Omit<Invitation, "status"> & {
  token: string;
  acceptedAt: string | null;
  revokedAt: string | null;
  emailSentCount: number;
  emailSentAt: number | null;
};

/** Same limits as claim_invitation_email (ingest attribution migration). */
const INVITE_EMAIL_MAX_SENDS = 5;
const INVITE_EMAIL_INTERVAL_MS = 60_000;

export type DemoProviderOptions = {
  /** Clock for time-based rules (invitation e-mail rate limit). */
  now?: () => number;
  /**
   * Simulates a broker that forgets a blocked device's credentials (EMQX Serverless): unblocking
   * then returns a new secret. Off by default.
   */
  unrevokeIssuesSecret?: boolean;
  /** Live map simulation step; 0 disables the simulation (tests drive it with tickLive). */
  liveTickMs?: number;
  /** Simulated device delay before a live watch is acknowledged (ms); 0 = at once. Default 1500. */
  liveAckMs?: number;
};

function randomHex(bytes: number): string {
  const a = new Uint8Array(bytes);
  if (globalThis.crypto?.getRandomValues) globalThis.crypto.getRandomValues(a);
  else for (let i = 0; i < a.length; i++) a[i] = Math.floor(Math.random() * 256);
  return Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("");
}

const newSecret = () => randomHex(32);
const newClaimCode = () => {
  const h = randomHex(6).toUpperCase();
  return `${h.slice(0, 4)}-${h.slice(4, 8)}-${h.slice(8, 12)}`;
};
/** Same normalisation as fts_norm_claim_code: keep hex digits, upper case, dashes every 4. */
export function normalizeClaimCode(code: string): string {
  const c = code.replace(/[^0-9A-Fa-f]/g, "").toUpperCase();
  return `${c.slice(0, 4)}-${c.slice(4, 8)}-${c.slice(8, 12)}`;
}

const clone = <T>(x: T): T => JSON.parse(JSON.stringify(x)) as T;

export class DemoProvider implements DataProvider {
  readonly mode = "demo" as const;
  private userId: string | null;

  // Per-instance copies so writes never leak between instances (tests, reloads).
  private tenants: Tenant[] = clone(TENANTS);
  private sites: Site[] = clone(SITES);
  private vehicles: Vehicle[] = clone(VEHICLES);
  private users: DemoUser[] = clone(USERS);
  private memberships: Membership[] = clone(MEMBERSHIPS);
  private devices: Device[];
  private live: LiveSnapshot[];
  private ops: OperatorData;
  private authlists = new Map<string, DemoAuthlist>();
  private liveListeners = new Set<(s: LiveSnapshot) => void>();
  private liveTimer: ReturnType<typeof setInterval> | null = null;
  /** ADR 0014 live watches: device id -> end (ms) and last request (ms, for the 30-s rate limit). */
  private liveWatches = new Map<string, { vehicleId: string; untilMs: number; requestedMs: number }>();
  private watchTimer: ReturnType<typeof setInterval> | null = null;
  /** Plain claim codes are fine in the demo; the database stores only hashes. */
  private claimCodes = new Map<string, string>(Object.entries(DEMO_CLAIM_CODES));
  private invitations: DemoInvitation[] = [];
  private seq = 0;
  private alertSettings: SiteAlertSettings[] = seedAlertSettings();
  private nightContacts: SiteNightContact[] = seedNightContacts();
  /** Tenants with alarm_copy_admins on (ADR 0011); off by default, like the database. */
  private alarmCopyAdmins = new Set<string>();
  private alarms: Alarm[];
  private notifications: AlarmNotification[];
  private locales = new Map<string, UiLocale>();
  /** Sprint 7b: push devices per user. The demo has no push server (no VAPID keys): push is reported disabled. */
  private pushDevices = new Map<string, PushDevice[]>();
  /** Demo/test switch: pretend the server has VAPID keys. */
  pushEnabled = false;
  private geofences: Geofence[] = seedGeofences(SITES);
  private checklist: DemoChecklist;
  /** Sprint 7a: module settings saved in this session (vehicle id -> settings; the demo device "acks" at once). */
  private moduleSettings = new Map<string, VehicleModuleSettings>();

  private readonly now: () => number;
  private readonly unrevokeIssuesSecret: boolean;

  constructor(
    private readonly delayMs = 120,
    options: DemoProviderOptions = {},
  ) {
    this.now = options.now ?? (() => Date.now());
    this.unrevokeIssuesSecret = options.unrevokeIssuesSecret ?? false;
    this.userId = this.readSession();
    this.devices = seedDevices(Date.now());
    this.live = withDemoBattery(seedLive(Date.now(), this.devices, this.sites, this.vehicles));
    this.ops = seedOperators(Date.now());
    const seeded = seedAlarms(Date.now(), this.sites, this.vehicles, this.memberships, this.alertSettings, this.nightContacts);
    this.alarms = seeded.alarms;
    this.notifications = seeded.notifications;
    for (const t of this.tenants) this.recomputeAuthlists(t.id, true);
    this.checklist = new DemoChecklist({
      me: () => this.me(),
      hasTenantRole: (t, min) => this.hasTenantRole(t, min),
      unscoped: (t, min) => this.unscoped(t, min),
      canAccessSite: (s) => this.canAccessSite(s),
      canManageSite: (s, min) => this.canManageSite(s, min),
      sites: () => this.sites,
      vehicles: () => this.vehicles,
      memberSeesSite: (u, t, s) => this.memberships.some((m) => m.userId === u && m.tenantId === t && memberSeesSite(this.sites, m, s)),
      displayName: (u) => this.users.find((x) => x.id === u)?.displayName ?? null,
      currentOperator: (vehicleId) => {
        const l = this.live.find((x) => x.vehicleId === vehicleId && x.operatorUid);
        const v = this.vehicles.find((x) => x.id === vehicleId);
        const c = l && v && this.ops.cards.find((x) => x.tenantId === v.tenantId && x.uid === l.operatorUid && x.status === "active");
        return c ? c.operatorId : null;
      },
      addAlarm: (a) => {
        this.alarms.push({ ...a, id: String(Math.max(0, ...this.alarms.map((x) => Number(x.id) || 0)) + 1) });
      },
      hadImpactSince: (vehicleId, since) => this.alarms.some((a) => a.vehicleId === vehicleId && a.type === "impact" && Date.parse(a.occurredAt) > since),
      now: () => this.now(),
      nextId: (prefix) => this.nextId(prefix),
    });
    this.liveTickMs = options.liveTickMs ?? 2000;
    this.liveAckMs = options.liveAckMs ?? 1500;
    this.reports = new DemoReports({
      now: () => this.now(),
      userId: () => this.userId,
      hasTenantRole: (t, min) => this.hasTenantRole(t, min),
      canAccessSite: (s) => this.canAccessSite(s),
      sites: () => this.sites,
      vehicles: () => this.vehicles,
      devices: () => this.devices,
      live: () => this.live,
      alarms: () => this.alarms,
      alarmVisible: (a) => this.alarmVisible(a),
      operators: () => this.ops,
      geofences: () => this.geofences,
      shiftsOf: (siteId) => this.checklist.effectiveShiftsOf(siteId),
      workHoursOf: (siteId) => effectiveAlertHours(this.sites, this.alertSettings, siteId).hours,
      faults: async (tenantId) => this.checklist.listFaults(tenantId, { limit: 1000 }),
      calibration: (vehicleId) => this.calibrationOf(vehicleId),
    });
    this.kys = new DemoKys({
      vehicles: () => this.vehicles,
      sites: () => this.sites,
      hasTenantRole: (t, min) => this.hasTenantRole(t, min),
      canAccessSite: (s) => this.canAccessSite(s),
      canManageSite: (s, min) => this.canManageSite(s, min),
      faults: (tenantId) => this.checklist.listFaults(tenantId, { limit: 1000 }),
      now: () => this.now(),
      nextId: (prefix) => this.nextId(prefix),
    });
  }

  /** Sprint 7b: sample reports and KPI panel (ADR 0018). */
  private reports: DemoReports;
  /** ADR 0033: FR-11 maintenance plan and records (sample). */
  private kys: DemoKys;

  private readonly liveAckMs: number;

  private readonly liveTickMs: number;

  private readSession(): string | null {
    try {
      const id = sessionStorage.getItem(SESSION_KEY) ?? localStorage.getItem(SESSION_KEY);
      return id && USERS.some((u) => u.id === id) ? id : null;
    } catch {
      return null;
    }
  }

  /** remember = false keeps the demo session in sessionStorage only, like the real provider (ADR 0020). */
  private writeSession(id: string | null, remember = true): void {
    try {
      localStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem(SESSION_KEY);
      if (id) (remember ? localStorage : sessionStorage).setItem(SESSION_KEY, id);
    } catch {
      // Session just won't survive a reload.
    }
  }

  private wait(): Promise<void> {
    return this.delayMs > 0 ? new Promise((r) => setTimeout(r, this.delayMs)) : Promise.resolve();
  }

  private nextId(prefix: string): string {
    this.seq += 1;
    return `${prefix}-${Date.now().toString(36)}-${this.seq}`;
  }

  private toCurrentUser(u: DemoUser): CurrentUser {
    return { ...u, memberships: this.memberships.filter((m) => m.userId === u.id).map((m) => ({ ...m, siteIds: [...m.siteIds] })) };
  }

  private me(): CurrentUser {
    const u = this.users.find((x) => x.id === this.userId);
    if (!u) throw new DataError("forbidden", "Not signed in");
    return this.toCurrentUser(u);
  }

  /* ---------- permission helpers (mirror the SQL helpers) ---------- */

  private isPlatformAdmin(): boolean {
    return this.me().platformAdmin;
  }

  private hasTenantRole(tenantId: string, min: TenantRole): boolean {
    const me = this.me();
    if (me.platformAdmin) return true;
    const m = me.memberships.find((x) => x.tenantId === tenantId);
    return !!m && roleAtLeast(m.role, min);
  }

  /** Site ids visible to the user in a tenant (a site grant includes all descendants). */
  private visibleSiteIds(tenantId: string): Set<string> {
    const me = this.me();
    const tenantSites = this.sites.filter((s) => s.tenantId === tenantId);
    if (me.platformAdmin) return new Set(tenantSites.map((s) => s.id));
    const m = me.memberships.find((x) => x.tenantId === tenantId);
    if (!m) return new Set();
    if (m.siteIds.length === 0) return new Set(tenantSites.map((s) => s.id));
    const out = new Set<string>(m.siteIds);
    let grew = true;
    while (grew) {
      grew = false;
      for (const s of tenantSites) {
        if (s.parentId && out.has(s.parentId) && !out.has(s.id)) {
          out.add(s.id);
          grew = true;
        }
      }
    }
    return out;
  }

  private canAccessSite(siteId: string): boolean {
    const s = this.sites.find((x) => x.id === siteId);
    return !!s && this.visibleSiteIds(s.tenantId).has(s.id);
  }

  private canManageSite(siteId: string, min: TenantRole): boolean {
    const s = this.sites.find((x) => x.id === siteId);
    return !!s && this.canAccessSite(siteId) && this.hasTenantRole(s.tenantId, min);
  }

  private requireMember(tenantId: string): void {
    if (!this.hasTenantRole(tenantId, "viewer")) throw new DataError("forbidden");
  }

  private deviceVisible(d: Device): boolean {
    if (this.isPlatformAdmin()) return true;
    if (d.vehicleId) {
      const v = this.vehicles.find((x) => x.id === d.vehicleId);
      return !!v && this.canAccessSite(v.siteId);
    }
    return d.tenantId !== null && this.hasTenantRole(d.tenantId, "fleet_manager");
  }

  /* ---------- session ---------- */

  async getCurrentUser(): Promise<CurrentUser | null> {
    await this.wait();
    return this.userId ? this.me() : null;
  }

  async signIn(email: string, password: string, options: SignInOptions = {}): Promise<CurrentUser> {
    await this.wait();
    const u = this.users.find((x) => x.email.toLowerCase() === email.trim().toLowerCase());
    if (!u || password !== DEMO_PASSWORD) throw new DataError("invalid_credentials");
    this.userId = u.id;
    this.writeSession(u.id, options.remember !== false);
    return this.toCurrentUser(u);
  }

  sessionRemembered(): boolean {
    try {
      return sessionStorage.getItem(SESSION_KEY) === null;
    } catch {
      return true;
    }
  }

  async sendSignInCode(email: string, _options?: SignInOptions): Promise<void> {
    await this.wait();
    // No e-mail in demo mode; unknown addresses succeed silently like the real provider.
    if (!EMAIL_PATTERN.test(email.trim())) throw new DataError("invalid_email");
  }

  async verifySignInCode(email: string, code: string, options: Pick<SignInOptions, "remember"> = {}): Promise<CurrentUser> {
    await this.wait();
    const u = this.users.find((x) => x.email.toLowerCase() === email.trim().toLowerCase());
    if (!u || code.trim() !== DEMO_SIGNIN_CODE) throw new DataError("invalid_code");
    this.userId = u.id;
    this.writeSession(u.id, options.remember !== false);
    return this.toCurrentUser(u);
  }

  async signInDemo(): Promise<CurrentUser> {
    await this.wait();
    this.userId = "u-demo";
    this.writeSession(this.userId);
    return this.me();
  }

  async signOut(): Promise<void> {
    this.userId = null;
    this.writeSession(null);
  }

  /* ---------- reads ---------- */

  async listTenants(): Promise<Tenant[]> {
    await this.wait();
    const me = this.me();
    const list = me.platformAdmin ? this.tenants : this.tenants.filter((t) => me.memberships.some((m) => m.tenantId === t.id));
    return list.map((t) => ({ ...t }));
  }

  async listSites(tenantId: string): Promise<Site[]> {
    await this.wait();
    this.requireMember(tenantId);
    const visible = this.visibleSiteIds(tenantId);
    return this.sites.filter((s) => s.tenantId === tenantId && visible.has(s.id)).map((s) => ({ ...s }));
  }

  async listVehicles(tenantId: string): Promise<Vehicle[]> {
    await this.wait();
    this.requireMember(tenantId);
    const visible = this.visibleSiteIds(tenantId);
    return this.vehicles
      .filter((v) => v.tenantId === tenantId && visible.has(v.siteId))
      .map((v) => ({ ...v, deviceId: this.devices.find((d) => d.vehicleId === v.id)?.deviceId ?? null }));
  }

  async listMembers(tenantId: string): Promise<Member[]> {
    await this.wait();
    if (!this.hasTenantRole(tenantId, "admin")) throw new DataError("forbidden");
    return this.memberships
      .filter((m) => m.tenantId === tenantId)
      .map((m) => {
        const u = this.users.find((x) => x.id === m.userId)!;
        return { userId: u.id, email: u.email, displayName: u.displayName, role: m.role, siteIds: [...m.siteIds] };
      });
  }

  /* ---------- fleet master data ---------- */

  async createTenant(input: { name: string; slug: string }): Promise<Tenant> {
    await this.wait();
    if (!this.isPlatformAdmin()) throw new DataError("forbidden");
    const name = input.name.trim();
    if (name.length < 2 || name.length > 120 || !SLUG_PATTERN.test(input.slug)) throw new DataError("unknown", "check constraint");
    if (this.tenants.some((t) => t.slug === input.slug)) throw new DataError("unknown", "duplicate slug");
    const t: Tenant = { id: this.nextId("t"), name, slug: input.slug, createdAt: new Date().toISOString() };
    this.tenants.push(t);
    return { ...t };
  }

  private isDescendant(candidateId: string, ancestorId: string): boolean {
    let cur = this.sites.find((s) => s.id === candidateId);
    const seen = new Set<string>();
    while (cur && !seen.has(cur.id)) {
      if (cur.id === ancestorId) return true;
      seen.add(cur.id);
      cur = cur.parentId ? this.sites.find((s) => s.id === cur!.parentId) : undefined;
    }
    return false;
  }

  async saveSite(input: SiteInput): Promise<Site> {
    await this.wait();
    const name = input.name.trim();
    const badCoords = (input.lat !== null && (input.lat < -90 || input.lat > 90)) || (input.lon !== null && (input.lon < -180 || input.lon > 180));
    if (name.length < 1 || name.length > 120 || badCoords) throw new DataError("unknown", "check constraint");
    if (input.parentId) {
      const parent = this.sites.find((s) => s.id === input.parentId);
      if (!parent || parent.tenantId !== input.tenantId) throw new DataError("unknown", "parent site must belong to the same tenant");
    }
    const fields = { name, kind: input.kind, address: input.address?.trim() || null, lat: input.lat, lon: input.lon };

    if (!input.id) {
      const ok =
        this.hasTenantRole(input.tenantId, "fleet_manager") &&
        (input.parentId === null ? this.hasTenantRole(input.tenantId, "admin") : this.canManageSite(input.parentId, "fleet_manager"));
      if (!ok) throw new DataError("forbidden");
      const s: Site = { id: this.nextId("s"), tenantId: input.tenantId, parentId: input.parentId, ...fields };
      this.sites.push(s);
      return { ...s };
    }

    const s = this.sites.find((x) => x.id === input.id);
    if (!s || !this.canManageSite(s.id, "fleet_manager")) throw new DataError("forbidden");
    if (s.tenantId !== input.tenantId) throw new DataError("unknown", "tenant_id is immutable");
    if (input.parentId !== s.parentId) {
      if (input.parentId === null && !this.hasTenantRole(s.tenantId, "admin")) throw new DataError("forbidden");
      if (input.parentId !== null && !this.canManageSite(input.parentId, "fleet_manager")) throw new DataError("forbidden");
      if (input.parentId !== null && this.isDescendant(input.parentId, s.id)) throw new DataError("unknown", "cycle");
    }
    Object.assign(s, fields, { parentId: input.parentId });
    return { ...s };
  }

  async deleteSite(siteId: string): Promise<void> {
    await this.wait();
    const s = this.sites.find((x) => x.id === siteId);
    if (!s || !this.canManageSite(siteId, "admin")) throw new DataError("forbidden");
    // The BEFORE DELETE trigger runs before the foreign key checks.
    const lastScope = this.memberships.some((m) => m.siteIds.includes(siteId) && m.siteIds.every((id) => id === siteId));
    if (lastScope) throw new DataError("site_is_last_scope");
    // Foreign keys are ON DELETE RESTRICT for child sites and vehicles.
    if (this.sites.some((x) => x.parentId === siteId) || this.vehicles.some((v) => v.siteId === siteId)) {
      throw new DataError("unknown", "site is referenced");
    }
    for (const m of this.memberships) m.siteIds = m.siteIds.filter((id) => id !== siteId);
    this.sites = this.sites.filter((x) => x.id !== siteId);
  }

  async saveVehicle(input: VehicleInput): Promise<Vehicle> {
    await this.wait();
    const fleetNo = input.fleetNo.trim();
    if (fleetNo.length < 1 || fleetNo.length > 40) throw new DataError("unknown", "check constraint");
    if (input.batteryVoltage !== null && !BATTERY_VOLTAGES.includes(input.batteryVoltage)) throw new DataError("unknown", "check constraint");
    const site = this.sites.find((s) => s.id === input.siteId);
    if (!site || site.tenantId !== input.tenantId) throw new DataError("forbidden");
    if (!this.canManageSite(input.siteId, "fleet_manager")) throw new DataError("forbidden");
    if (this.vehicles.some((v) => v.tenantId === input.tenantId && v.fleetNo === fleetNo && v.id !== input.id)) {
      throw new DataError("unknown", "duplicate fleet_no");
    }
    const fields = {
      siteId: input.siteId,
      fleetNo,
      type: input.type,
      power: input.power,
      make: input.make?.trim() || null,
      model: input.model?.trim() || null,
      serialNo: input.serialNo?.trim() || null,
      batteryVoltage: input.batteryVoltage,
      active: input.active,
      unauthorizedStartAlarm: input.unauthorizedStartAlarm,
    };
    if (!input.id) {
      const v: Vehicle = { id: this.nextId("v"), tenantId: input.tenantId, deviceId: null, ...fields };
      this.vehicles.push(v);
      this.checklist.ensureToken(v.id);
      return { ...v };
    }
    const v = this.vehicles.find((x) => x.id === input.id);
    if (!v || !this.canManageSite(v.siteId, "fleet_manager")) throw new DataError("forbidden");
    if (v.tenantId !== input.tenantId) throw new DataError("unknown", "tenant_id is immutable");
    Object.assign(v, fields);
    return { ...v, deviceId: this.devices.find((d) => d.vehicleId === v.id)?.deviceId ?? null };
  }

  /* ---------- devices ---------- */

  async listDevices(tenantId: string): Promise<Device[]> {
    await this.wait();
    this.requireMember(tenantId);
    return this.devices.filter((d) => d.tenantId === tenantId && this.deviceVisible(d)).map((d) => ({ ...d }));
  }

  async listInventory(): Promise<Device[]> {
    await this.wait();
    if (!this.isPlatformAdmin()) throw new DataError("forbidden");
    return this.devices.map((d) => ({ ...d }));
  }

  async registerDevices(input: DeviceRegistration[]): Promise<RegistrationResult[]> {
    await this.wait();
    if (!this.isPlatformAdmin()) throw new DataError("forbidden");
    // Validate the whole batch first so a failure registers nothing.
    const seen = new Set<string>();
    for (const r of input) {
      if (!DEVICE_ID_PATTERN.test(r.deviceId)) throw new DataError("invalid_device_id", r.deviceId);
      // ADR 0034 (fts_devices_teltonika_guard): TLT-<IMEI> with a valid IMEI; the IMEI column must match.
      if (r.deviceId.startsWith("TLT-")) {
        const imei = imeiOfDeviceId(r.deviceId);
        if (!imei || !isValidImei(imei) || (r.imei?.trim() && r.imei.trim() !== imei)) throw new DataError("invalid_imei", r.deviceId);
      }
      if (seen.has(r.deviceId) || this.devices.some((d) => d.deviceId === r.deviceId)) throw new DataError("device_exists", r.deviceId);
      seen.add(r.deviceId);
    }
    return input.map((r) => {
      const secret = newSecret();
      const claimCode = newClaimCode();
      this.devices.push({
        deviceId: r.deviceId,
        tenantId: null,
        vehicleId: null,
        hw: r.hw?.trim() || null,
        fw: null,
        imei: r.imei?.trim() || null,
        iccid: r.iccid?.trim() || null,
        online: false,
        lastSeenAt: null,
        claimedAt: null,
        revoked: false,
        hasCardReader: deviceHasCardReader(r.deviceId, r.hw?.trim() || null),
      });
      this.claimCodes.set(r.deviceId, claimCode);
      return { deviceId: r.deviceId, secret, claimCode, brokerSynced: true };
    });
  }

  private findDeviceForAdmin(deviceId: string): Device {
    if (!this.isPlatformAdmin()) throw new DataError("forbidden");
    const d = this.devices.find((x) => x.deviceId === deviceId);
    if (!d) throw new DataError("device_not_found");
    return d;
  }

  async rotateDeviceSecret(deviceId: string): Promise<RotationResult> {
    await this.wait();
    const d = this.findDeviceForAdmin(deviceId);
    // Same rule as the database: a blocked device gets no new secret (unblock first).
    if (d.revoked) throw new DataError("device_revoked");
    return { secret: newSecret(), brokerSynced: true };
  }

  async setDeviceRevoked(deviceId: string, revoked: boolean): Promise<RevocationResult> {
    await this.wait();
    const d = this.findDeviceForAdmin(deviceId);
    const wasRevoked = d.revoked;
    d.revoked = revoked;
    if (revoked) d.online = false;
    if (!revoked && wasRevoked && this.unrevokeIssuesSecret) return { secret: newSecret(), brokerSynced: true };
    return { brokerSynced: true };
  }

  async releaseDevice(deviceId: string): Promise<{ claimCode: string; brokerSynced: boolean }> {
    await this.wait();
    const d = this.findDeviceForAdmin(deviceId);
    const code = newClaimCode();
    d.vehicleId = null;
    d.tenantId = null;
    d.claimedAt = null;
    this.claimCodes.set(deviceId, code);
    return { claimCode: code, brokerSynced: true };
  }

  async claimDevice(tenantId: string, deviceId: string, claimCode: string): Promise<void> {
    await this.wait();
    if (!this.userId || !this.hasTenantRole(tenantId, "fleet_manager")) throw new DataError("forbidden");
    const id = deviceId.trim().toUpperCase();
    const d = this.devices.find((x) => x.deviceId === id);
    const code = this.claimCodes.get(id);
    // One uniform error for unknown device, wrong code, already claimed or revoked.
    if (!d || d.tenantId !== null || d.revoked || !code || code !== normalizeClaimCode(claimCode)) {
      throw new DataError("claim_invalid");
    }
    d.tenantId = tenantId;
    d.claimedAt = new Date().toISOString();
    this.claimCodes.delete(id);
  }

  async pairDevice(deviceId: string, vehicleId: string): Promise<void> {
    await this.wait();
    const v = this.vehicles.find((x) => x.id === vehicleId);
    if (!v || !this.canManageSite(v.siteId, "fleet_manager")) throw new DataError("forbidden");
    const d = this.devices.find((x) => x.deviceId === deviceId);
    if (!d || d.tenantId !== v.tenantId) throw new DataError("tenant_mismatch");
    if (d.revoked) throw new DataError("device_revoked");
    if (!v.active) throw new DataError("vehicle_inactive");
    if (d.vehicleId === v.id) return;
    if (d.vehicleId !== null) throw new DataError("device_busy");
    if (this.devices.some((x) => x.vehicleId === v.id)) throw new DataError("vehicle_busy");
    d.vehicleId = v.id;
  }

  async unpairDevice(deviceId: string): Promise<void> {
    await this.wait();
    const d = this.devices.find((x) => x.deviceId === deviceId);
    if (!d || d.tenantId === null || !this.hasTenantRole(d.tenantId, "viewer")) throw new DataError("forbidden");
    if (d.vehicleId === null) return;
    const v = this.vehicles.find((x) => x.id === d.vehicleId);
    if (!v || !this.canManageSite(v.siteId, "fleet_manager")) throw new DataError("forbidden");
    d.vehicleId = null;
  }

  /* ---------- members and invitations ---------- */

  /** memberships_update / memberships_delete policies (ADR 0005 admin seats). */
  private canTouchMember(tenantId: string, target: Membership, newRole?: TenantRole): boolean {
    if (this.isPlatformAdmin()) return true;
    return (
      this.hasTenantRole(tenantId, "admin") &&
      target.userId !== this.userId &&
      target.role !== "admin" &&
      (newRole === undefined || newRole !== "admin")
    );
  }

  async updateMemberRole(tenantId: string, userId: string, role: TenantRole): Promise<void> {
    await this.wait();
    const m = this.memberships.find((x) => x.tenantId === tenantId && x.userId === userId);
    if (!m || !this.canTouchMember(tenantId, m, role)) throw new DataError("forbidden");
    // check_scope_role: admins are tenant-wide by definition.
    if (role === "admin" && m.siteIds.length > 0) throw new DataError("invalid_scope");
    m.role = role;
  }

  async removeMember(tenantId: string, userId: string): Promise<void> {
    await this.wait();
    const m = this.memberships.find((x) => x.tenantId === tenantId && x.userId === userId);
    if (!m || !this.canTouchMember(tenantId, m)) throw new DataError("forbidden");
    this.memberships = this.memberships.filter((x) => x !== m);
  }

  private static status(i: DemoInvitation, now = Date.now()): InvitationStatus {
    if (i.acceptedAt) return "accepted";
    if (i.revokedAt) return "revoked";
    if (Date.parse(i.expiresAt) < now) return "expired";
    return "pending";
  }

  private static publicInvitation(i: DemoInvitation): Invitation {
    return {
      id: i.id,
      tenantId: i.tenantId,
      email: i.email,
      role: i.role,
      siteIds: [...i.siteIds],
      createdAt: i.createdAt,
      expiresAt: i.expiresAt,
      status: DemoProvider.status(i),
    };
  }

  async listInvitations(tenantId: string): Promise<Invitation[]> {
    await this.wait();
    if (!this.hasTenantRole(tenantId, "admin")) throw new DataError("forbidden");
    return this.invitations
      .filter((i) => i.tenantId === tenantId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .map(DemoProvider.publicInvitation);
  }

  async createInvitation(input: InvitationInput): Promise<{ invitation: Invitation; token: string }> {
    await this.wait();
    if (!this.userId || !(this.isPlatformAdmin() || (this.hasTenantRole(input.tenantId, "admin") && input.role !== "admin"))) {
      throw new DataError("forbidden");
    }
    const email = input.email.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(email) || email.length > 254) throw new DataError("invalid_email");
    const siteIds = [...new Set(input.siteIds)];
    if ((input.role === "admin" && siteIds.length > 0) || siteIds.some((id) => !this.sites.some((s) => s.id === id && s.tenantId === input.tenantId))) {
      throw new DataError("invalid_scope");
    }
    const existing = this.users.find((u) => u.email.toLowerCase() === email);
    if (existing && this.memberships.some((m) => m.tenantId === input.tenantId && m.userId === existing.id)) {
      throw new DataError("already_member");
    }
    const now = new Date();
    for (const i of this.invitations) {
      if (i.tenantId === input.tenantId && i.email === email && !i.acceptedAt && !i.revokedAt) i.revokedAt = now.toISOString();
    }
    const token = newSecret();
    const inv: DemoInvitation = {
      id: this.nextId("inv"),
      tenantId: input.tenantId,
      email,
      role: input.role,
      siteIds,
      createdAt: now.toISOString(),
      expiresAt: new Date(now.getTime() + INVITE_TTL_MS).toISOString(),
      token,
      acceptedAt: null,
      revokedAt: null,
      emailSentCount: 0,
      emailSentAt: null,
    };
    this.invitations.push(inv);
    return { invitation: DemoProvider.publicInvitation(inv), token };
  }

  async revokeInvitation(invitationId: string): Promise<void> {
    await this.wait();
    const i = this.invitations.find((x) => x.id === invitationId);
    if (!i || !this.userId || !(this.isPlatformAdmin() || (this.hasTenantRole(i.tenantId, "admin") && i.role !== "admin"))) {
      throw new DataError("forbidden");
    }
    if (!i.acceptedAt && !i.revokedAt) i.revokedAt = new Date().toISOString();
  }

  /** No e-mail leaves the demo; the rights, token and rate-limit checks match claim_invitation_email. */
  async sendInvitationEmail(invitationId: string, token: string): Promise<void> {
    await this.wait();
    const i = this.invitations.find((x) => x.id === invitationId);
    if (!i || !this.userId || !(this.isPlatformAdmin() || (this.hasTenantRole(i.tenantId, "admin") && i.role !== "admin"))) {
      throw new DataError("forbidden");
    }
    if (i.token !== token || DemoProvider.status(i, this.now()) !== "pending") throw new DataError("invitation_invalid");
    const now = this.now();
    if (i.emailSentCount >= INVITE_EMAIL_MAX_SENDS || (i.emailSentAt !== null && now - i.emailSentAt < INVITE_EMAIL_INTERVAL_MS)) {
      throw new DataError("rate_limited");
    }
    i.emailSentCount += 1;
    i.emailSentAt = now;
  }

  async previewInvitation(token: string): Promise<InvitationPreview | null> {
    await this.wait();
    const i = this.invitations.find((x) => x.token === token);
    if (!i) return null;
    const tenant = this.tenants.find((t) => t.id === i.tenantId);
    return { tenantName: tenant?.name ?? "", role: i.role, status: DemoProvider.status(i), expiresAt: i.expiresAt };
  }

  async acceptInvitation(token: string): Promise<string> {
    await this.wait();
    if (!this.userId) throw new DataError("forbidden");
    const i = this.invitations.find((x) => x.token === token);
    if (!i || DemoProvider.status(i) !== "pending") throw new DataError("invitation_invalid");
    // Demo accounts count as verified e-mail addresses.
    const me = this.me();
    if (me.email.toLowerCase() !== i.email) throw new DataError("wrong_email");
    if (this.memberships.some((m) => m.tenantId === i.tenantId && m.userId === me.id)) throw new DataError("already_member");
    if (i.siteIds.some((id) => !this.sites.some((s) => s.id === id && s.tenantId === i.tenantId))) throw new DataError("scope_changed");
    this.memberships.push({ tenantId: i.tenantId, userId: me.id, role: i.role, siteIds: [...i.siteIds] });
    i.acceptedAt = new Date().toISOString();
    return i.tenantId;
  }

  async sendSignInLink(email: string, _redirectTo: string, _options?: Pick<SignInOptions, "captchaToken">): Promise<void> {
    await this.wait();
    // No e-mail is sent in demo mode; the UI explains this.
    if (!EMAIL_PATTERN.test(email.trim())) throw new DataError("invalid_email");
  }

  async changePassword(_newPassword: string): Promise<void> {
    await this.wait();
    // Demo accounts share one fixed password; there is nothing to change.
    throw new DataError("not_in_demo");
  }

  async updateDisplayName(displayName: string): Promise<void> {
    await this.wait();
    const u = this.users.find((x) => x.id === this.userId);
    if (!u) throw new DataError("forbidden");
    const name = displayName.trim();
    if (name) u.displayName = name;
  }

  /* ---------- live map ---------- */

  async listLive(tenantId: string): Promise<LiveSnapshot[]> {
    await this.wait();
    this.requireMember(tenantId);
    const visible = new Set(this.devices.filter((d) => d.tenantId === tenantId && d.vehicleId && this.deviceVisible(d)).map((d) => d.deviceId));
    return this.live.filter((l) => visible.has(l.deviceId) && this.devices.find((d) => d.deviceId === l.deviceId)?.vehicleId === l.vehicleId).map((l) => ({ ...l }));
  }

  subscribeLive(onChange: (s: LiveSnapshot) => void, onStatus?: (s: LiveFeedStatus) => void): Unsubscribe {
    this.liveListeners.add(onChange);
    onStatus?.("live");
    if (!this.liveTimer && this.liveTickMs > 0) this.liveTimer = setInterval(() => this.tickLive(), this.liveTickMs);
    return () => {
      this.liveListeners.delete(onChange);
      if (this.liveListeners.size === 0 && this.liveTimer) {
        clearInterval(this.liveTimer);
        this.liveTimer = null;
      }
      if (this.liveListeners.size === 0 && this.watchTimer) {
        clearInterval(this.watchTimer);
        this.watchTimer = null;
      }
    };
  }

  /* ---------- Sprint 7a: battery and lift modules ---------- */

  async getVehicleModuleData(vehicleId: string): Promise<VehicleModuleData> {
    await this.wait();
    const v = this.vehicles.find((x) => x.id === vehicleId);
    if (!v || !this.canAccessSite(v.siteId)) throw new DataError("forbidden");
    const d = this.devices.find((x) => x.vehicleId === v.id) ?? null;
    const snap = d ? this.live.find((l) => l.deviceId === d.deviceId && l.vehicleId === v.id) : undefined;
    return demoModuleData(d?.deviceId ?? null, this.now(), this.moduleSettings.get(v.id), snap?.batAt ? snap : undefined);
  }

  async saveModuleSettings(input: ModuleSettingsInput): Promise<void> {
    await this.wait();
    const v = this.vehicles.find((x) => x.id === input.vehicleId);
    if (!v || !this.canManageSite(v.siteId, "fleet_manager")) throw new DataError("forbidden");
    const ok = (x: number | null, lo: number, hi: number) => x === null || (Number.isFinite(x) && x >= lo && x <= hi);
    if (
      !ok(input.batAh, 10, 5000) || !ok(input.liftKgPerBar, 0.1, 1000) || !ok(input.liftBarThreshold, 1, 250) ||
      !ok(input.liftEmptyBar, 0, 100) || !ok(input.ratedCapacityKg, 100, 50_000) ||
      (input.batType !== null && input.batType !== "lead_acid" && input.batType !== "lithium")
    ) {
      throw new DataError("unknown", "check constraint");
    }
    const { vehicleId: _id, ...settings } = input;
    this.moduleSettings.set(v.id, settings);
  }

  /* ---------- Sprint 7b: reports and the KPI panel (sample data, ADR 0018) ---------- */

  async reportUsage(q: ReportQuery): Promise<ReportResult<UsageRow>> {
    await this.wait();
    return this.reports.reportUsage(q);
  }

  async reportEvents(q: EventReportQuery): Promise<ReportResult<EventRow>> {
    await this.wait();
    return this.reports.reportEvents(q);
  }

  async reportRoute(q: RouteQuery): Promise<ReportResult<RoutePoint>> {
    await this.wait();
    return this.reports.reportRoute(q);
  }

  async reportChecklist(q: ReportQuery): Promise<ReportResult<ChecklistComplianceRow>> {
    await this.wait();
    return this.reports.reportChecklist(q);
  }

  async reportHeatmap(q: HeatmapQuery): Promise<HeatmapResult> {
    await this.wait();
    return this.reports.reportHeatmap(q);
  }

  async exportHeatmap(q: HeatmapQuery, format: ExportFormat): Promise<HeatmapResult> {
    await this.wait();
    return this.reports.exportHeatmap(q, format);
  }

  async reportSpaghetti(q: SpaghettiQuery): Promise<SpaghettiResult> {
    await this.wait();
    return this.reports.reportSpaghetti(q);
  }

  async exportSpaghetti(q: SpaghettiQuery, format: ExportFormat): Promise<SpaghettiResult> {
    await this.wait();
    return this.reports.exportSpaghetti(q, format);
  }

  async reportDriving(q: DrivingQuery): Promise<DrivingResult> {
    await this.wait();
    return this.reports.reportDriving(q);
  }

  async exportDriving(q: DrivingQuery, format: ExportFormat): Promise<DrivingResult> {
    await this.wait();
    return this.reports.exportDriving(q, format);
  }

  async getKpiPanel(tenantId: string, siteId: string | null, period: KpiPeriod): Promise<KpiPanel> {
    await this.wait();
    return this.reports.getKpiPanel(tenantId, siteId, period);
  }

  async exportUsage(q: ReportQuery, format: ExportFormat): Promise<ReportResult<UsageRow>> {
    await this.wait();
    return this.reports.exportUsage(q, format);
  }

  async exportEvents(q: EventReportQuery, format: ExportFormat): Promise<ReportResult<EventRow>> {
    await this.wait();
    return this.reports.exportEvents(q, format);
  }

  async exportRoute(q: RouteQuery, format: ExportFormat): Promise<ReportResult<RoutePoint>> {
    await this.wait();
    return this.reports.exportRoute(q, format);
  }

  async exportChecklist(q: ReportQuery, format: ExportFormat): Promise<ReportResult<ChecklistComplianceRow>> {
    await this.wait();
    return this.reports.exportChecklist(q, format);
  }

  /** Exports logged in this demo session (tests and the demo audit view). */
  exportLog(): readonly (ReportExportEntry & { userId: string; at: string })[] {
    return this.reports.exportLog;
  }

  /** One simulation step: moving vehicles drive a little, every vehicle that is online reports. */
  tickLive(): void {
    const nowIso = new Date(this.now()).toISOString();
    for (const l of this.live) {
      const d = this.devices.find((x) => x.deviceId === l.deviceId);
      if (!d?.online || d.revoked) continue;
      const moving = l.ignition === true && (l.speedKmh ?? 0) >= 1;
      const indoors = l.posAt !== null && Date.parse(l.receivedAt ?? l.ts) - Date.parse(l.posAt) > 60_000;
      if (moving && l.lat !== null && l.lon !== null) {
        const step = this.seq++ % 8 < 4 ? 1 : -1;
        l.lat += step * 0.00004;
        l.lon += 0.00005;
        l.posAt = nowIso;
      } else if (!indoors && l.lat !== null) {
        l.posAt = nowIso;
      }
      l.ts = nowIso;
      l.receivedAt = nowIso;
      d.lastSeenAt = nowIso;
      for (const fn of this.liveListeners) fn({ ...l });
    }
  }

  /* ---------- ADR 0014: live watch (mirrors request_live_watch) ---------- */

  async startLiveWatch(vehicleId: string, durS: number = LIVE_WATCH.defaultS): Promise<LiveWatch> {
    await this.wait();
    if (!Number.isInteger(durS) || durS < LIVE_WATCH.minS || durS > LIVE_WATCH.maxS) throw new DataError("bad_request");
    const v = this.vehicles.find((x) => x.id === vehicleId);
    const d = v && this.devices.find((x) => x.vehicleId === v.id);
    if (!v || !d || !this.canAccessSite(v.siteId)) throw new DataError("device_not_found");
    if (!this.canManageSite(v.siteId, "safety")) throw new DataError("forbidden");
    if (d.revoked) throw new DataError("device_revoked");
    if (!d.online) throw new DataError("device_offline");
    const now = this.now();
    const prev = this.liveWatches.get(d.deviceId);
    if (prev && now - prev.requestedMs < LIVE_WATCH.minGapS * 1000) throw new DataError("rate_limited");
    const running = [...this.liveWatches].filter(([id, w]) => id !== d.deviceId && w.untilMs > now && this.vehicles.find((x) => x.id === w.vehicleId)?.tenantId === v.tenantId);
    if (running.length >= LIVE_WATCH.maxPerTenant) throw new DataError("too_many_live_watches");
    const untilMs = now + durS * 1000;
    this.liveWatches.set(d.deviceId, { vehicleId: v.id, untilMs, requestedMs: now });
    // Simulated device: the watched vehicle reports every second (the others keep the normal step).
    if (!this.watchTimer && this.liveTickMs > 0 && this.liveListeners.size > 0) this.watchTimer = setInterval(() => this.tickWatched(), LIVE_WATCH.intervalS * 1000);
    return { vehicleId: v.id, deviceId: d.deviceId, until: new Date(untilMs).toISOString(), acked: this.liveAckMs === 0 };
  }

  async listLiveWatches(tenantId: string): Promise<LiveWatch[]> {
    await this.wait();
    this.requireMember(tenantId);
    const now = this.now();
    const out: LiveWatch[] = [];
    for (const [deviceId, w] of this.liveWatches) {
      const v = this.vehicles.find((x) => x.id === w.vehicleId);
      const d = this.devices.find((x) => x.deviceId === deviceId);
      if (w.untilMs > now && v && v.tenantId === tenantId && d?.vehicleId === v.id && this.canAccessSite(v.siteId)) {
        out.push({ vehicleId: v.id, deviceId, until: new Date(w.untilMs).toISOString(), acked: now - w.requestedMs >= this.liveAckMs });
      }
    }
    return out;
  }

  /** One-second step for vehicles under live watch (demo of the 1-s reporting). */
  tickWatched(): void {
    const now = this.now();
    const nowIso = new Date(now).toISOString();
    for (const [deviceId, w] of [...this.liveWatches]) {
      if (w.untilMs <= now) continue;
      const l = this.live.find((x) => x.deviceId === deviceId);
      const d = this.devices.find((x) => x.deviceId === deviceId);
      if (!l || !d?.online || d.revoked) continue;
      if (l.ignition === true && (l.speedKmh ?? 0) >= 1 && l.lat !== null && l.lon !== null) {
        l.lat += 0.00001;
        l.lon += 0.000012;
        l.posAt = nowIso;
      }
      l.ts = nowIso;
      l.receivedAt = nowIso;
      d.lastSeenAt = nowIso;
      for (const fn of this.liveListeners) fn({ ...l });
    }
    if (![...this.liveWatches.values()].some((w) => w.untilMs > now) && this.watchTimer) {
      clearInterval(this.watchTimer);
      this.watchTimer = null;
    }
  }

  /* ---------- Sprint 5: operators (mirror RLS + triggers of the operators migration) ---------- */

  private today(): string {
    return businessDate(new Date(this.now()));
  }

  private dayOffset(n: number): string {
    return new Date(Date.parse(`${this.today()}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
  }

  private operatorVisible(o: Operator): boolean {
    return this.hasTenantRole(o.tenantId, "viewer") && (o.siteId === null || this.canAccessSite(o.siteId));
  }

  /** Mirrors can_manage_operator: a tenant-wide operator (no site) needs a member without a site scope. */
  private canWriteOperator(o: Pick<Operator, "tenantId" | "siteId">): boolean {
    if (o.siteId !== null) return this.canManageSite(o.siteId, "safety") && this.sites.some((s) => s.id === o.siteId && s.tenantId === o.tenantId);
    const me = this.me();
    if (me.platformAdmin) return true;
    const m = me.memberships.find((x) => x.tenantId === o.tenantId);
    return !!m && roleAtLeast(m.role, "safety") && m.siteIds.length === 0;
  }

  /** Operator for a child write: visible, safety+, not anonymised. */
  private writableOperator(operatorId: string): Operator {
    const o = this.ops.operators.find((x) => x.id === operatorId);
    if (!o || !this.operatorVisible(o) || !this.canWriteOperator(o)) throw new DataError("forbidden");
    if (o.anonymized) throw new DataError("operator_left");
    return o;
  }

  /** Recompute every device list of a tenant; online devices confirm at once, offline ones stay pending. */
  private recomputeAuthlists(tenantId: string, initial = false): void {
    const nowIso = new Date(this.now()).toISOString();
    const today = this.today();
    const input = { ...this.ops, sites: this.sites };
    // ADR 0023: a device without a card reader gets no list.
    for (const d of this.devices.filter((x) => x.tenantId === tenantId && !x.revoked && x.hasCardReader)) {
      const v = d.vehicleId ? this.vehicles.find((x) => x.id === d.vehicleId && x.active) : undefined;
      const entries = v ? authorizedEntries(v, input, today) : [];
      const hash = JSON.stringify(entries);
      const cur = this.authlists.get(d.deviceId);
      if (cur && cur.hash === hash) continue;
      const ver = (cur?.ver ?? 0) + 1;
      const acked = d.online || initial;
      this.authlists.set(d.deviceId, {
        deviceId: d.deviceId,
        ver,
        hash,
        changedAt: nowIso,
        sentVer: ver,
        sentAt: nowIso,
        ackedVer: acked ? ver : (cur?.ackedVer ?? null),
        ackedAt: acked ? nowIso : (cur?.ackedAt ?? null),
        error: null,
      });
    }
  }

  async listOperatorData(tenantId: string): Promise<OperatorData> {
    await this.wait();
    this.requireMember(tenantId);
    const operators = this.ops.operators.filter((o) => o.tenantId === tenantId && this.operatorVisible(o));
    const ids = new Set(operators.map((o) => o.id));
    const safety = this.hasTenantRole(tenantId, "safety");
    return clone({
      operators,
      cards: safety ? this.ops.cards.filter((c) => ids.has(c.operatorId)) : [],
      documents: safety ? this.ops.documents.filter((d) => ids.has(d.operatorId)) : [],
      permissions: this.ops.permissions.filter((p) => ids.has(p.operatorId)),
    });
  }

  async saveOperator(input: OperatorInput): Promise<Operator> {
    await this.wait();
    const fullName = input.fullName.trim();
    const employeeNo = input.employeeNo?.trim() || null;
    if (input.siteId && !this.sites.some((s) => s.id === input.siteId && s.tenantId === input.tenantId)) throw new DataError("tenant_mismatch");
    if (!fullName) throw new DataError("bad_request");
    const prevLeft = input.id ? this.ops.operators.find((x) => x.id === input.id)?.leftOn ?? null : null;
    if (input.leftOn && input.leftOn !== prevLeft && input.leftOn < this.dayOffset(-31)) throw new DataError("invalid_left_on");
    const target = { tenantId: input.tenantId, siteId: input.siteId };
    if (employeeNo && this.ops.operators.some((o) => o.tenantId === input.tenantId && o.employeeNo === employeeNo && !o.anonymized && o.id !== input.id)) {
      throw new DataError("bad_request", "employee number in use");
    }
    if (input.id) {
      const o = this.ops.operators.find((x) => x.id === input.id);
      if (!o || !this.operatorVisible(o) || !this.canWriteOperator(o) || !this.canWriteOperator(target)) throw new DataError("forbidden");
      if (o.anonymized) throw new DataError("operator_left");
      Object.assign(o, { siteId: input.siteId, fullName, employeeNo, active: input.active, leftOn: input.leftOn });
      this.recomputeAuthlists(o.tenantId);
      return clone(o);
    }
    if (!this.canWriteOperator(target)) throw new DataError("forbidden");
    const o: Operator = { id: this.nextId("op"), tenantId: input.tenantId, siteId: input.siteId, fullName, employeeNo, active: input.active, leftOn: input.leftOn, anonymized: false };
    this.ops.operators.push(o);
    return clone(o);
  }

  async addCard(input: CardInput): Promise<OperatorCard> {
    await this.wait();
    const o = this.writableOperator(input.operatorId);
    const uid = normalizeCardUid(input.uid);
    if (!uid || uid !== input.uid || !(CARD_TECHS as readonly string[]).includes(input.tech)) throw new DataError("invalid_card");
    if (this.ops.cards.some((c) => c.tenantId === o.tenantId && c.uid === uid && c.status === "active")) throw new DataError("card_in_use");
    const c: OperatorCard = { id: this.nextId("c"), tenantId: o.tenantId, operatorId: o.id, uid, tech: input.tech, status: "active", createdAt: new Date(this.now()).toISOString() };
    this.ops.cards.push(c);
    this.recomputeAuthlists(o.tenantId);
    return clone(c);
  }

  async setCardStatus(cardId: string, status: Exclude<CardStatus, "active">): Promise<void> {
    await this.wait();
    const c = this.ops.cards.find((x) => x.id === cardId);
    if (!c) throw new DataError("forbidden");
    this.writableOperator(c.operatorId);
    c.status = status;
    this.recomputeAuthlists(c.tenantId);
  }

  async saveDocument(input: DocumentInput): Promise<OperatorDocument> {
    await this.wait();
    const o = this.writableOperator(input.operatorId);
    if (!(DOCUMENT_KINDS as readonly string[]).includes(input.kind) || !/^\d{4}-\d{2}-\d{2}$/.test(input.validUntil)) throw new DataError("bad_request");
    const fields = { kind: input.kind, label: input.label?.trim() || null, validUntil: input.validUntil, required: input.required };
    let d: OperatorDocument;
    if (input.id) {
      const cur = this.ops.documents.find((x) => x.id === input.id && x.operatorId === o.id);
      if (!cur) throw new DataError("forbidden");
      d = Object.assign(cur, fields);
    } else {
      d = { id: this.nextId("d"), tenantId: o.tenantId, operatorId: o.id, ...fields };
      this.ops.documents.push(d);
    }
    this.recomputeAuthlists(o.tenantId);
    return clone(d);
  }

  async deleteDocument(documentId: string): Promise<void> {
    await this.wait();
    const d = this.ops.documents.find((x) => x.id === documentId);
    if (!d) throw new DataError("forbidden");
    this.writableOperator(d.operatorId);
    this.ops.documents = this.ops.documents.filter((x) => x.id !== documentId);
    this.recomputeAuthlists(d.tenantId);
  }

  async addPermission(input: PermissionInput): Promise<OperatorPermission> {
    await this.wait();
    const o = this.writableOperator(input.operatorId);
    if ((input.siteId === null) === (input.vehicleId === null) || (input.vehicleId !== null && input.vehicleType !== null)) throw new DataError("invalid_permission");
    if (input.vehicleType !== null && !(VEHICLE_TYPES as readonly string[]).includes(input.vehicleType)) throw new DataError("invalid_permission");
    const siteId = input.siteId ?? this.vehicles.find((v) => v.id === input.vehicleId && v.tenantId === o.tenantId)?.siteId;
    if (!siteId || !this.sites.some((s) => s.id === siteId && s.tenantId === o.tenantId)) throw new DataError("tenant_mismatch");
    if (!this.canManageSite(siteId, "safety")) throw new DataError("forbidden");
    const p: OperatorPermission = { id: this.nextId("p"), tenantId: o.tenantId, operatorId: o.id, siteId: input.siteId, vehicleType: input.vehicleType, vehicleId: input.vehicleId, validUntil: input.validUntil };
    this.ops.permissions.push(p);
    this.recomputeAuthlists(o.tenantId);
    return clone(p);
  }

  async deletePermission(permissionId: string): Promise<void> {
    await this.wait();
    const p = this.ops.permissions.find((x) => x.id === permissionId);
    if (!p) throw new DataError("forbidden");
    this.writableOperator(p.operatorId);
    const siteId = p.siteId ?? this.vehicles.find((v) => v.id === p.vehicleId)?.siteId;
    if (!siteId || !this.canManageSite(siteId, "safety")) throw new DataError("forbidden");
    this.ops.permissions = this.ops.permissions.filter((x) => x.id !== permissionId);
    this.recomputeAuthlists(p.tenantId);
  }

  /* ---- ADR 0027: recorder health (SAMPLE DATA: one fictional recorder on the first paired vehicle) ---- */

  private dvrSettings = new Map<string, DvrSettings>();

  async listDeviceHealth(tenantId: string): Promise<DeviceHealth[]> {
    await this.wait();
    this.requireMember(tenantId);
    const devs = this.devices.filter((d) => d.tenantId === tenantId && this.deviceVisible(d));
    // The sample recorder: the tenant's first paired device, as if it were a bridged m-DVR (fictional states).
    // Teltonika units (ADR 0034) have no recorder.
    const sample = devs.find((d) => d.vehicleId && !deviceIsTeltonika(d.deviceId, d.hw))?.deviceId;
    return devs.map((d) => {
      const settings = this.dvrSettings.get(d.deviceId) ?? { medium: null, cameras: null };
      const isDvr = !d.hasCardReader || d.deviceId === sample;
      const effective = effectiveDvrSettings(settings, isDvr);
      if (d.deviceId !== sample || !d.vehicleId) {
        return { deviceId: d.deviceId, vehicleId: d.vehicleId, reportedAt: null, cameras: [], ipcs: [], storage: {}, videoLoss: null, settings, effective, issues: [] };
      }
      const now = this.now();
      const cameras = [0, 3, 0, 0, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2];
      const storage = { ssd: 1, sd: 0, usb: 1, usb2: 1 };
      const issues = [
        ...(effective.medium && storage[effective.medium] >= 2 ? [{ key: "storage", state: storage[effective.medium], since: new Date(now - 25 * 60_000).toISOString(), lastAt: new Date(now - 40_000).toISOString(), alarmed: true }] : []),
        ...effective.cameras.filter((c) => cameras[c - 1] !== 0).map((c) => ({ key: `cam${c}`, state: cameras[c - 1] ?? -1, since: new Date(now - 18 * 60_000).toISOString(), lastAt: new Date(now - 40_000).toISOString(), alarmed: true })),
      ];
      return { deviceId: d.deviceId, vehicleId: d.vehicleId, reportedAt: new Date(now - 40_000).toISOString(), cameras, ipcs: [2, 2, 2, 2, 2, 2, 2, 2], storage, videoLoss: 0, settings, effective, issues };
    });
  }

  /* ---- ADR 0034: Teltonika units and analog calibration (sample data) ---- */

  private readonly calibrations = new Map<string, VehicleAnalogCalibration>();

  /** Sample Teltonika vehicles come with the sample wiring (AIN2 battery divider 1:4, AIN1 lift sensor). */
  private calibrationOf(vehicleId: string): VehicleAnalogCalibration {
    const stored = this.calibrations.get(vehicleId);
    if (stored) return stored;
    const v = this.vehicles.find((x) => x.id === vehicleId);
    const d = this.devices.find((x) => x.vehicleId === vehicleId);
    // The low-battery alert stays off until the owner confirms a default (the console suggests a value).
    if (v && d && deviceIsTeltonika(d.deviceId, d.hw)) return { ...SAMPLE_CALIBRATION };
    return defaultAnalogCalibration();
  }

  private visibleDevice(deviceId: string): Device {
    const d = this.devices.find((x) => x.deviceId === deviceId);
    const v = d?.vehicleId ? this.vehicles.find((x) => x.id === d.vehicleId) : undefined;
    if (!d || !(this.isPlatformAdmin() || (d.tenantId !== null && this.hasTenantRole(d.tenantId, "viewer") && (!v || this.canAccessSite(v.siteId))))) throw new DataError("forbidden");
    return d;
  }

  async getTeltonikaStatus(deviceId: string): Promise<TeltonikaSyncStatus> {
    await this.wait();
    const d = this.visibleDevice(deviceId);
    if (!deviceIsTeltonika(d.deviceId, d.hw)) throw new DataError("device_not_found");
    if (!this.isPlatformAdmin() && !(d.tenantId && this.hasTenantRole(d.tenantId, "safety"))) throw new DataError("forbidden");
    const list = this.authlists.get(d.deviceId);
    const ago = (min: number) => new Date(this.now() - min * 60_000).toISOString();
    // Sample: in sync like the card-list badge; the second unit had one timeout earlier that a retry resolved.
    const second = d.deviceId.endsWith("7526");
    return {
      deviceId, hasCardReader: d.hasCardReader, listVer: list?.ver ?? 1, ackedVer: list?.ver ?? 1,
      ackedAt: ago(18), sentAt: ago(18), inSync: true, cards: 3,
      pendingAdd: 0, pendingRemove: 0, slotsUnknown: 0, lastSyncAt: ago(18),
      lastFailure: second ? { at: ago(26 * 60), error: "timeout" } : null, failures: 0, retryAt: null,
      reconciledAt: ago(9 * 60), safetyCheckedAt: ago(9 * 60), unsafe: [],
    };
  }

  async getImmobilizer(deviceId: string): Promise<DeviceImmobilizer> {
    await this.wait();
    const d = this.visibleDevice(deviceId);
    if (!deviceIsTeltonika(d.deviceId, d.hw)) return { state: null, at: null };
    const live = this.live.find((l) => l.deviceId === deviceId);
    return { state: live?.ignition ? "authorized" : "no_card", at: live?.ts ?? null };
  }

  async getAnalogCalibration(vehicleId: string): Promise<AnalogCalibrationRecord> {
    await this.wait();
    const v = this.vehicles.find((x) => x.id === vehicleId);
    if (!v || !(this.isPlatformAdmin() || this.canAccessSite(v.siteId))) throw new DataError("forbidden");
    const d = this.devices.find((x) => x.vehicleId === vehicleId);
    // The sample Teltonika vehicles come with a saved sample calibration.
    return { ...this.calibrationOf(vehicleId), stored: this.calibrations.has(vehicleId) || (!!d && deviceIsTeltonika(d.deviceId, d.hw)) };
  }

  async saveAnalogCalibration(vehicleId: string, cal: VehicleAnalogCalibration): Promise<void> {
    await this.wait();
    const v = this.vehicles.find((x) => x.id === vehicleId);
    if (!v || !(this.isPlatformAdmin() || this.canManageSite(v.siteId, "fleet_manager"))) throw new DataError("forbidden");
    if (calibrationIssues(cal).length > 0) throw new DataError("invalid_calibration");
    this.calibrations.set(vehicleId, { ...cal });
  }

  async reportBattery(q: BatteryQuery): Promise<BatteryResult> {
    await this.wait();
    return this.reports.reportBattery(q);
  }
  async exportBattery(q: BatteryQuery, format: ExportFormat): Promise<BatteryResult> {
    await this.wait();
    return this.reports.exportBattery(q, format);
  }
  async reportLift(q: TelematicsQuery): Promise<LiftResult> {
    await this.wait();
    return this.reports.reportLift(q);
  }
  async exportLift(q: TelematicsQuery, format: ExportFormat): Promise<LiftResult> {
    await this.wait();
    return this.reports.exportLift(q, format);
  }
  async reportEco(q: TelematicsQuery): Promise<EcoResult> {
    await this.wait();
    return this.reports.reportEco(q);
  }
  async exportEco(q: TelematicsQuery, format: ExportFormat): Promise<EcoResult> {
    await this.wait();
    return this.reports.exportEco(q, format);
  }

  async setDeviceDvrSettings(deviceId: string, settings: DvrSettings): Promise<void> {
    await this.wait();
    const d = this.devices.find((x) => x.deviceId === deviceId);
    const v = d?.vehicleId ? this.vehicles.find((x) => x.id === d.vehicleId) : undefined;
    const ok = !!d && (this.isPlatformAdmin() || (d.tenantId !== null && (v ? this.canManageSite(v.siteId, "fleet_manager") : this.hasTenantRole(d.tenantId, "fleet_manager"))));
    if (!ok) throw new DataError("forbidden");
    if ((settings.medium !== null && !(DVR_MEDIA as readonly string[]).includes(settings.medium)) || (settings.cameras && settings.cameras.some((c) => !Number.isInteger(c) || c < 1 || c > 16))) {
      throw new DataError("invalid_dvr_settings");
    }
    this.dvrSettings.set(deviceId, { medium: settings.medium, cameras: settings.cameras ? [...new Set(settings.cameras)].sort((a, b) => a - b) : null });
  }

  async listAuthlistStatus(tenantId: string): Promise<AuthlistStatus[]> {
    await this.wait();
    this.requireMember(tenantId);
    return this.devices
      .filter((d) => d.tenantId === tenantId && this.deviceVisible(d))
      .flatMap((d) => {
        const a = this.authlists.get(d.deviceId);
        if (!a) return [];
        const { hash: _hash, ...status } = a;
        return [{ ...status }];
      });
  }

  async syncAuthlists(tenantId: string): Promise<{ sent: number; failed: number }> {
    await this.wait();
    if (!this.hasTenantRole(tenantId, "safety")) throw new DataError("forbidden");
    const before = new Map([...this.authlists].map(([k, v]) => [k, v.ver]));
    this.recomputeAuthlists(tenantId);
    let sent = 0;
    for (const [k, v] of this.authlists) if (before.get(k) !== v.ver) sent++;
    return { sent, failed: 0 };
  }

  async listLiveOperators(tenantId: string): Promise<LiveOperator[]> {
    await this.wait();
    this.requireMember(tenantId);
    const out: LiveOperator[] = [];
    for (const l of this.live) {
      const d = this.devices.find((x) => x.deviceId === l.deviceId);
      const v = this.vehicles.find((x) => x.id === l.vehicleId);
      if (!d || !v || d.vehicleId !== v.id || v.tenantId !== tenantId || !this.canAccessSite(v.siteId) || !l.operatorUid) continue;
      const c = this.ops.cards.find((x) => x.tenantId === tenantId && x.uid === l.operatorUid && x.status === "active");
      const o = c && this.ops.operators.find((x) => x.id === c.operatorId && !x.anonymized);
      if (o) out.push({ deviceId: l.deviceId, operatorId: o.id, fullName: o.fullName });
    }
    return out;
  }

  /* ---------- Sprint 6a: alarms and notification settings (mirror ADR 0011 RLS) ---------- */

  /** Members without a site scope (and platform admins): see tenant-wide rows such as alarms of a deleted site. */
  private unscoped(tenantId: string, min: TenantRole): boolean {
    const me = this.me();
    if (me.platformAdmin) return true;
    const m = me.memberships.find((x) => x.tenantId === tenantId);
    return !!m && roleAtLeast(m.role, min) && m.siteIds.length === 0;
  }

  /** Mirrors alarms_select: an alarm whose site was deleted is visible to members without a scope. */
  private alarmVisible(a: Alarm): boolean {
    return this.hasTenantRole(a.tenantId, "viewer") && (a.siteId === null ? this.unscoped(a.tenantId, "viewer") : this.canAccessSite(a.siteId));
  }

  /** Mirrors acknowledge_alarm's site rule. */
  private canAckAlarm(a: Alarm): boolean {
    return a.siteId === null ? this.unscoped(a.tenantId, "safety") : this.canManageSite(a.siteId, "safety");
  }

  async listAlarms(tenantId: string, query: AlarmQuery = {}): Promise<Alarm[]> {
    await this.wait();
    this.requireMember(tenantId);
    const from = query.from ? Date.parse(query.from) : null;
    const to = query.to ? Date.parse(query.to) : null;
    return clone(
      this.alarms
        .filter((a) => a.tenantId === tenantId && this.alarmVisible(a))
        .filter((a) => !query.status || a.status === query.status)
        .filter((a) => !query.type || a.type === query.type)
        .filter((a) => !query.vehicleId || a.vehicleId === query.vehicleId)
        .filter((a) => !query.siteIds || (a.siteId !== null && query.siteIds.includes(a.siteId)))
        .filter((a) => from === null || Date.parse(a.occurredAt) >= from)
        .filter((a) => to === null || Date.parse(a.occurredAt) < to)
        .sort((a, b) => Date.parse(b.occurredAt) - Date.parse(a.occurredAt) || Number(b.id) - Number(a.id))
        .slice(0, query.limit ?? 500),
    );
  }

  async getAlarm(tenantId: string, alarmId: string): Promise<Alarm | null> {
    await this.wait();
    const a = this.alarms.find((x) => x.id === alarmId && x.tenantId === tenantId);
    return a && this.alarmVisible(a) ? clone(a) : null;
  }

  async countOpenAlarms(tenantId: string): Promise<number> {
    await this.wait();
    this.requireMember(tenantId);
    return this.alarms.filter((a) => a.tenantId === tenantId && a.status === "open" && this.alarmVisible(a)).length;
  }

  async listAlarmNotifications(alarmId: string): Promise<AlarmNotification[]> {
    await this.wait();
    const a = this.alarms.find((x) => x.id === alarmId);
    if (!a || !this.alarmVisible(a)) return [];
    const me = this.me().id;
    const all = this.canAckAlarm(a);
    return clone(this.notifications.filter((n) => n.alarmId === alarmId && (all || n.userId === me)));
  }

  async acknowledgeAlarm(alarmId: string, note: string | null): Promise<void> {
    await this.wait();
    const a = this.alarms.find((x) => x.id === alarmId);
    if (!a || !this.alarmVisible(a) || !this.canAckAlarm(a)) throw new DataError("forbidden");
    // Same order as acknowledge_alarm(): already acknowledged first, then the note length.
    if (a.status === "acknowledged") throw new DataError("already_acknowledged");
    const trimmed = note?.trim() || null;
    if (trimmed && trimmed.length > ACK_NOTE_MAX) throw new DataError("note_too_long");
    Object.assign(a, { status: "acknowledged", ackedBy: this.me().id, ackedAt: new Date(this.now()).toISOString(), ackNote: trimmed });
  }

  async listAlertSettings(tenantId: string): Promise<SiteAlertSettings[]> {
    await this.wait();
    this.requireMember(tenantId);
    return clone(this.alertSettings.filter((x) => x.tenantId === tenantId && this.canAccessSite(x.siteId)));
  }

  async saveAlertSettings(input: AlertSettingsInput): Promise<SiteAlertSettings> {
    await this.wait();
    const site = this.sites.find((s) => s.id === input.siteId);
    if (!site || site.tenantId !== input.tenantId || !this.canManageSite(site.id, "fleet_manager")) throw new DataError("forbidden");
    const days = [...new Set(input.workDays)].sort((a, b) => a - b);
    const start = parseHm(input.workStart);
    const end = parseHm(input.workEnd);
    // Same rule as the database guard: start in [00:00, 24:00), end after it, at most 24:00.
    if (days.length === 0 || days.some((d) => !Number.isInteger(d) || d < 1 || d > 7) || Number.isNaN(start) || Number.isNaN(end) || start >= end || start >= 1440) {
      throw new DataError("invalid_alert_settings");
    }
    const row: SiteAlertSettings = { tenantId: site.tenantId, siteId: site.id, workDays: days, workStart: input.workStart, workEnd: input.workEnd, tz: ALERT_TIME_ZONE };
    this.alertSettings = [...this.alertSettings.filter((x) => x.siteId !== site.id), row];
    return clone(row);
  }

  async resetAlertSettings(siteId: string): Promise<void> {
    await this.wait();
    if (!this.canManageSite(siteId, "fleet_manager") || !this.alertSettings.some((x) => x.siteId === siteId)) throw new DataError("forbidden");
    this.alertSettings = this.alertSettings.filter((x) => x.siteId !== siteId);
  }

  async getEffectiveAlertSettings(siteId: string): Promise<EffectiveAlertSettings | null> {
    await this.wait();
    if (!this.canAccessSite(siteId)) return null;
    // Like the database: inherit even from ancestors the user cannot see.
    const { hours, sourceSiteId } = effectiveAlertHours(this.sites, this.alertSettings, siteId);
    return { hours, sourceSiteId };
  }

  async listContactCandidates(siteId: string): Promise<ContactCandidate[]> {
    await this.wait();
    const site = this.sites.find((s) => s.id === siteId);
    if (!site || !this.canManageSite(siteId, "fleet_manager")) throw new DataError("forbidden");
    return this.memberships
      .filter((m) => m.tenantId === site.tenantId && memberSeesSite(this.sites, m, siteId))
      .map((m) => ({ userId: m.userId, displayName: this.users.find((u) => u.id === m.userId)?.displayName ?? "—", role: m.role }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName, "tr"));
  }

  async listNightContacts(tenantId: string): Promise<SiteNightContact[]> {
    await this.wait();
    this.requireMember(tenantId);
    return clone(this.nightContacts.filter((x) => x.tenantId === tenantId && this.canAccessSite(x.siteId)));
  }

  async setNightContacts(tenantId: string, siteId: string, userIds: string[]): Promise<void> {
    await this.wait();
    const site = this.sites.find((s) => s.id === siteId);
    if (!site || site.tenantId !== tenantId || !this.canManageSite(siteId, "fleet_manager")) throw new DataError("forbidden");
    const wanted = [...new Set(userIds)];
    const members = this.memberships.filter((m) => m.tenantId === tenantId);
    for (const u of wanted) {
      const m = members.find((x) => x.userId === u);
      if (!m || !memberSeesSite(this.sites, m, siteId)) throw new DataError("invalid_contact");
    }
    this.nightContacts = [...this.nightContacts.filter((c) => c.siteId !== siteId), ...wanted.map((userId) => ({ tenantId, siteId, userId }))];
  }

  async getEffectiveNightContacts(siteId: string): Promise<EffectiveNightContacts> {
    await this.wait();
    const site = this.sites.find((s) => s.id === siteId);
    if (!site || !this.canAccessSite(siteId)) return { contacts: [], sourceSiteId: null };
    // Like effective_night_contacts(): resolved through every ancestor, visible or not; only the source id is returned.
    const members = this.memberships.filter((m) => m.tenantId === site.tenantId);
    const r = effectiveNightContacts(this.sites, this.nightContacts, siteId, nightChecks(this.sites, members, this.users, siteId));
    return { contacts: this.names(r.userIds), sourceSiteId: r.userIds.length > 0 ? r.sourceSiteId : null };
  }

  async getAlertRecipientsStatus(siteId: string): Promise<AlertRecipientsStatus | null> {
    await this.wait();
    const site = this.sites.find((s) => s.id === siteId);
    if (!site || !this.canAccessSite(siteId)) return null;
    const members = this.memberships.filter((m) => m.tenantId === site.tenantId);
    return recipientsStatus(this.sites, members, this.users, this.nightContacts, siteId, this.alarmCopyAdmins.has(site.tenantId));
  }

  async getAlarmCopyAdmins(tenantId: string): Promise<boolean> {
    await this.wait();
    return this.hasTenantRole(tenantId, "viewer") && this.alarmCopyAdmins.has(tenantId);
  }

  async setAlarmCopyAdmins(tenantId: string, on: boolean): Promise<void> {
    await this.wait();
    // Like set_tenant_alarm_copy_admins(): tenant admins and platform admins only.
    if (!this.tenants.some((t) => t.id === tenantId) || !this.hasTenantRole(tenantId, "admin")) throw new DataError("forbidden");
    if (on) this.alarmCopyAdmins.add(tenantId);
    else this.alarmCopyAdmins.delete(tenantId);
  }

  async listAlarmPeople(alarmId: string): Promise<PersonName[]> {
    await this.wait();
    const a = this.alarms.find((x) => x.id === alarmId);
    if (!a || !this.alarmVisible(a)) return [];
    const ids = this.notifications.filter((n) => n.alarmId === alarmId).flatMap((n) => (n.userId ? [n.userId] : []));
    return this.names(a.ackedBy ? [...ids, a.ackedBy] : ids);
  }

  /** Display names (only) of users, sorted like the SQL functions. */
  private names(userIds: string[]): PersonName[] {
    return [...new Set(userIds)]
      .map((userId) => ({ userId, displayName: this.users.find((u) => u.id === userId)?.displayName || "—" }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName, "tr") || a.userId.localeCompare(b.userId));
  }

  async listAlertPeople(tenantId: string): Promise<AlertPerson[]> {
    await this.wait();
    this.requireMember(tenantId);
    const me = this.me().id;
    const all = this.hasTenantRole(tenantId, "admin");
    return this.memberships
      .filter((m) => m.tenantId === tenantId && (all || m.userId === me))
      .map((m) => ({ userId: m.userId, displayName: this.users.find((u) => u.id === m.userId)?.displayName ?? m.userId, role: m.role, siteIds: [...m.siteIds] }));
  }

  async updateLocale(locale: UiLocale): Promise<void> {
    await this.wait();
    if (locale !== "tr" && locale !== "en") throw new DataError("bad_request");
    this.locales.set(this.me().id, locale);
  }

  /* ---------- Sprint 7b: web push (ADR 0019; mirrors push_subscribe and the own-rows RLS) ---------- */

  async getPushConfig(): Promise<PushConfig> {
    await this.wait();
    return this.pushEnabled ? { enabled: true, publicKey: "BDemoOnlyNotARealKey" } : { enabled: false, publicKey: null };
  }

  async listPushDevices(): Promise<PushDevice[]> {
    await this.wait();
    return [...(this.pushDevices.get(this.me().id) ?? [])];
  }

  async savePushSubscription(input: PushSubscriptionInput): Promise<string> {
    await this.wait();
    if (!validPushSubscription(input)) throw new DataError("invalid_push_subscription");
    const me = this.me().id;
    // The same browser moves to the current user (new row); otherwise refresh.
    for (const [uid, list] of this.pushDevices) if (uid !== me) this.pushDevices.set(uid, list.filter((d) => d.endpoint !== input.endpoint));
    const mine = this.pushDevices.get(me) ?? [];
    const existing = mine.find((d) => d.endpoint === input.endpoint);
    if (existing) {
      existing.label = input.label;
      existing.failureCount = 0;
      existing.lastError = null;
      return existing.id;
    }
    if (mine.length >= PUSH_MAX_DEVICES) throw new DataError("push_device_limit");
    const d: PushDevice = { id: `push-${Math.random().toString(16).slice(2, 10)}`, endpoint: input.endpoint, label: input.label, createdAt: new Date().toISOString(), lastSuccessAt: null, lastFailureAt: null, failureCount: 0, lastError: null };
    this.pushDevices.set(me, [...mine, d]);
    return d.id;
  }

  async removePushDevice(id: string): Promise<void> {
    await this.wait();
    const me = this.me().id;
    const mine = this.pushDevices.get(me) ?? [];
    if (!mine.some((d) => d.id === id)) throw new DataError("forbidden");
    this.pushDevices.set(me, mine.filter((d) => d.id !== id));
  }

  async sendTestPush(): Promise<PushTestResult> {
    await this.wait();
    if (!this.pushEnabled) throw new DataError("push_disabled");
    const mine = this.pushDevices.get(this.me().id) ?? [];
    const now = new Date().toISOString();
    for (const d of mine) d.lastSuccessAt = now;
    return { sent: mine.length, failed: 0, removed: 0 };
  }

  /** Demo/test helper: the stored e-mail language of a user. */
  localeOf(userId: string): UiLocale | null {
    return this.locales.get(userId) ?? null;
  }

  /* ---------- Sprint 6b: geofences (mirror ADR 0015 RLS) ---------- */

  /** Like fts_geofence_readable_site(): sites the user sees, plus the ancestors of a scoped user's sites. */
  private geofenceVisible(g: Geofence): boolean {
    if (this.canAccessSite(g.siteId)) return true;
    const m = this.me().memberships.find((x) => x.tenantId === g.tenantId);
    if (!m) return false;
    for (const start of m.siteIds) {
      let cur = this.sites.find((s) => s.id === start);
      for (let depth = 0; cur && depth <= 32; depth++) {
        if (cur.id === g.siteId) return true;
        cur = cur.parentId ? this.sites.find((s) => s.id === cur!.parentId) : undefined;
      }
    }
    return false;
  }

  async listGeofences(tenantId: string, siteId?: string): Promise<Geofence[]> {
    await this.wait();
    this.requireMember(tenantId);
    return clone(
      this.geofences
        .filter((g) => g.tenantId === tenantId && (siteId === undefined || g.siteId === siteId) && this.geofenceVisible(g))
        .sort((a, b) => a.name.localeCompare(b.name, "tr")),
    );
  }

  async getGeofence(geofenceId: string): Promise<Geofence | null> {
    await this.wait();
    const g = this.geofences.find((x) => x.id === geofenceId);
    return g && this.geofenceVisible(g) ? clone(g) : null;
  }

  async saveGeofence(input: GeofenceInput): Promise<Geofence> {
    await this.wait();
    const existing = input.id ? this.geofences.find((x) => x.id === input.id) : undefined;
    if (input.id && (!existing || !this.geofenceVisible(existing))) throw new DataError("forbidden");
    const siteId = existing?.siteId ?? input.siteId;
    const site = this.sites.find((x) => x.id === siteId);
    if (!site || site.tenantId !== input.tenantId || !this.canManageSite(siteId, "safety")) throw new DataError("forbidden");
    if (validateGeofence(input, VEHICLE_TYPES).length > 0) throw new DataError("invalid_geofence");
    if (!existing && this.geofences.filter((g) => g.siteId === siteId).length >= GEOFENCE_LIMITS.perSite) throw new DataError("too_many_geofences");
    const circle = input.shape === "circle";
    const now = new Date(this.now()).toISOString();
    const g: Geofence = {
      id: existing?.id ?? this.nextId("gf"),
      tenantId: site.tenantId,
      siteId,
      name: normalizeGeofenceName(input.name),
      kind: input.kind,
      shape: input.shape,
      coords: circle ? null : (input.coords ?? []).map((c) => [c[0], c[1]] as LonLat),
      centerLat: circle ? input.centerLat : null,
      centerLon: circle ? input.centerLon : null,
      radiusM: circle ? input.radiusM : null,
      vehicleTypes: [...new Set(input.vehicleTypes)],
      dwellLimitS: input.kind === "dwell" ? input.dwellLimitS : null,
      active: input.active,
      createdBy: existing?.createdBy ?? this.me().id,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    };
    if (existing) Object.assign(existing, g);
    else this.geofences.push(g);
    return clone(g);
  }

  async deleteGeofence(geofenceId: string): Promise<void> {
    await this.wait();
    const g = this.geofences.find((x) => x.id === geofenceId);
    if (!g || !this.geofenceVisible(g) || !this.canManageSite(g.siteId, "safety")) throw new DataError("forbidden");
    this.geofences = this.geofences.filter((x) => x.id !== geofenceId);
    // Assumed: alarms.geofence_id references the zone ON DELETE SET NULL; the alarm and its point stay.
    for (const a of this.alarms) if (a.geofenceId === geofenceId) a.geofenceId = null;
  }

  /* ---------- Sprint 7a: pre-shift checklist (ADR 0016; rules in demo-checklist.ts) ---------- */

  async getChecklistForm(token: string): Promise<ChecklistForm> {
    await this.wait();
    return this.checklist.form(token);
  }

  async uploadChecklistPhoto(token: string, file: Blob): Promise<string> {
    await this.wait();
    return this.checklist.uploadPhoto(token, file);
  }

  async submitChecklist(token: string, input: ChecklistSubmitInput): Promise<ChecklistSubmitResult> {
    await this.wait();
    return this.checklist.submit(token, input);
  }

  async listShifts(tenantId: string): Promise<SiteShift[]> {
    await this.wait();
    this.requireMember(tenantId);
    return this.checklist.listShifts(tenantId);
  }

  async saveShift(input: ShiftInput): Promise<SiteShift> {
    await this.wait();
    return this.checklist.saveShift(input);
  }

  async deleteShift(shiftId: string): Promise<void> {
    await this.wait();
    this.checklist.deleteShift(shiftId);
  }

  async listChecklistRecipients(tenantId: string): Promise<ChecklistRecipient[]> {
    await this.wait();
    this.requireMember(tenantId);
    return this.checklist.listRecipients(tenantId);
  }

  async addChecklistRecipient(input: ChecklistRecipientInput): Promise<ChecklistRecipient> {
    await this.wait();
    return this.checklist.addRecipient(input);
  }

  async deleteChecklistRecipient(recipientId: string): Promise<void> {
    await this.wait();
    this.checklist.deleteRecipient(recipientId);
  }

  async getEffectiveChecklistRecipients(siteId: string): Promise<EffectiveChecklistRecipients> {
    await this.wait();
    return this.checklist.effectiveRecipients(siteId);
  }

  async listChecklistTemplates(tenantId: string): Promise<ChecklistTemplate[]> {
    await this.wait();
    return this.checklist.listTemplates(tenantId);
  }

  async copyChecklistTemplate(tenantId: string, templateId: string, name: string | null): Promise<string> {
    await this.wait();
    return this.checklist.copyTemplate(tenantId, templateId, name);
  }

  async saveChecklistTemplate(input: ChecklistTemplateInput): Promise<void> {
    await this.wait();
    this.checklist.saveTemplate(input);
  }

  async deleteChecklistTemplate(templateId: string): Promise<void> {
    await this.wait();
    this.checklist.deleteTemplate(templateId);
  }

  async listChecklistAssignments(tenantId: string): Promise<ChecklistAssignment[]> {
    await this.wait();
    return this.checklist.listAssignments(tenantId);
  }

  async setChecklistAssignment(tenantId: string, category: ChecklistCategory, templateId: string | null): Promise<void> {
    await this.wait();
    this.checklist.setAssignment(tenantId, category, templateId);
  }

  async listChecklistSubmissions(tenantId: string, query?: SubmissionQuery): Promise<ChecklistSubmission[]> {
    await this.wait();
    this.requireMember(tenantId);
    return this.checklist.listSubmissions(tenantId, query);
  }

  async getChecklistSubmission(submissionId: string): Promise<ChecklistSubmissionDetail | null> {
    await this.wait();
    return this.checklist.getSubmission(submissionId);
  }

  async listChecklistFaults(tenantId: string, query?: FaultQuery): Promise<ChecklistFault[]> {
    await this.wait();
    this.requireMember(tenantId);
    return this.checklist.listFaults(tenantId, query);
  }

  async resolveChecklistFault(faultId: string, note: string | null): Promise<void> {
    await this.wait();
    this.checklist.resolveFault(faultId, note);
  }

  async checklistPhotoUrl(path: string): Promise<string> {
    await this.wait();
    return this.checklist.photoUrl(path);
  }

  async listChecklistTokens(tenantId: string): Promise<ChecklistToken[]> {
    await this.wait();
    return this.checklist.listTokens(tenantId);
  }

  async rotateChecklistToken(vehicleId: string): Promise<string> {
    await this.wait();
    return this.checklist.rotateToken(vehicleId);
  }

  /* ---- ADR 0033: KYS forms ---- */

  async reportKysFr11(q: KysFr11Query): Promise<ReportResult<KysFr11Row>> {
    await this.wait();
    return this.kys.fr11(q, KYS_DEMO_TZ);
  }

  async exportKysFr11(q: KysFr11Query, format: ExportFormat): Promise<ReportResult<KysFr11Row>> {
    await this.wait();
    const { tenantId, ...f } = q;
    return this.reports.logged(tenantId, "kys_fr11", format, f, async () => this.kys.fr11(q, KYS_DEMO_TZ), () => false);
  }

  async reportKysFr12(q: KysFr12Query): Promise<ReportResult<KysFr12Form>> {
    await this.wait();
    return this.checklist.kysFr12(q, KYS_DEMO_TZ);
  }

  async exportKysFr12(q: KysFr12Query, format: ExportFormat): Promise<ReportResult<KysFr12Form>> {
    await this.wait();
    const { tenantId, ...f } = q;
    return this.reports.logged(tenantId, "kys_fr12", format, f, async () => this.checklist.kysFr12(q, KYS_DEMO_TZ), () => false);
  }

  async approveChecklistSubmission(submissionId: string): Promise<{ approvedAt: string; approvedRole: KysApproverRole }> {
    await this.wait();
    return this.checklist.approve(submissionId);
  }

  async setChecklistTemplateForm(templateId: string, form: KysFormRef | null): Promise<void> {
    await this.wait();
    this.checklist.setTemplateForm(templateId, form);
  }

  async listMaintenanceRecords(vehicleId: string): Promise<MaintenanceRecord[]> {
    await this.wait();
    return this.kys.listRecords(vehicleId);
  }

  async saveMaintenancePlan(input: MaintenancePlanInput): Promise<void> {
    await this.wait();
    this.kys.savePlan(input);
  }

  async addMaintenanceRecord(input: MaintenanceRecordInput): Promise<MaintenanceRecord> {
    await this.wait();
    return this.kys.addRecord(input);
  }

  async getFormFooter(tenantId: string): Promise<string | null> {
    await this.wait();
    return this.kys.footer(tenantId);
  }

  async setFormFooter(tenantId: string, text: string | null): Promise<void> {
    await this.wait();
    this.kys.setFooter(tenantId, text);
  }
}

/** The demo sites are all in Türkiye. */
const KYS_DEMO_TZ = "Europe/Istanbul";
